import { createSignal, createEffect, onCleanup, onMount, untrack, on, For, Show } from "solid-js";
import { sanitizeTooltipHtml } from '@utils/htmlSanitization';
import { Portal } from "solid-js/web";
import * as d3 from "d3";

import Loading from "../../utilities/Loading";

import { persistantStore } from "../../../store/persistantStore";

import { selectedEvents, setSelectedEvents, setLegendSelectionActive, triggerUpdate, setTriggerUpdate, triggerSelection, setTriggerSelection, setHasSelection, setSelection, isEventHidden, selectedRange, legendSelectionActive, highlightedEventIds } from "../../../store/selectionStore";
import { tooltip, setTooltip, eventType, filtered, color, phase, tabledata } from "../../../store/globalStore";
import { MANEUVER_EVENT_TYPES } from "../../../utils/maneuverEventType";
import { selectedGradesManeuvers, setSelectedGradesManeuvers } from "../../../store/filterStore";

import { myTickFormat, putData } from "../../../utils/global";
import { maneuverVideoElapsedSeconds, setManeuverVideoElapsedSeconds, requestManeuverVideoSeek, setManeuverPlayheadSeconds, clearManeuverPlayhead } from "../../../store/playbackStore";
import { isMacOS } from "../../../utils/deviceDetection";
import { error as logError, debug } from "../../../utils/console";
import { buildManeuverPaths, computeManeuverPathsOffThread } from "../../../utils/maneuverPathsBuild";
import { fetchTimeSeriesData } from "../../../services/maneuversDataService";
import {
  buildTimeseriesDescription,
  fetchManeuverTimeseriesOptions,
  isManeuverTimeseriesChartChannel,
  parseTimeSeriesResponse,
} from "../../../utils/maneuverTimeseriesGroups";
import { buildColorGrouping } from "../../../utils/colorGrouping";
import { resolveItemColor } from "../../../utils/selectionColors";
import { isDark } from "../../../store/themeStore";
import { apiEndpoints } from "../../../config/env";
import { user } from "../../../store/userStore";
import { shouldBlockGradeLikeHotkeysForPerformanceSelections } from "../../../utils/performanceLocalSelection";
import { maneuverColorLegendRenderedPx } from "../../../utils/maneuverLegendHeight";
import { maneuverBaselineStore } from "../../../store/maneuverBaselineStore";
import { baselineTimeseriesChannelExtent, baselineTimeseriesForChannel, readBaselineTimeseriesValue } from "../../../utils/maneuverBaselineTracks";

/**
 * MAP side panel: extra height on top of the sibling map's height. fitParent measures the map element
 * itself, so 0 already makes the chart exactly as tall as the map — this is only a nudge if you want
 * it to run slightly past. The pages add the same value to the toggle button's offset, so raising it
 * keeps the button clear of the chart automatically. One knob, tune here.
 */
export const MAP_SIDE_PANEL_EXTRA_HEIGHT_PX = 0;

/** Gap between the chart and its toggle button — matches the map caption's mt-2 (8px). */
export const MAP_SIDE_PANEL_BUTTON_GAP_PX = 8;

function escapeSelectorId(id: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(id);
  }
  return id.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

interface TimeSeriesProps {
  context?: 'dataset' | 'fleet' | 'historical';
  /** When omitted, component runs multi-group lazy sections (BASICS + options from API). */
  description?: string;
  /** Force multi-group mode even if description is passed (optional). */
  multiGroup?: boolean;
  onDataUpdate?: () => void;
  /** Prefix for chart DOM ids when multiple TimeSeries instances exist (e.g. maneuver window). */
  instanceId?: string;
  /** Live solo panels: when false, skip triggerUpdate-driven fetches while hidden. */
  panelActive?: () => boolean;
  /** Live poll gate: notify parent when timeseries fetch loading state changes. */
  onLoadStateChange?: (loading: boolean) => void;
  /** Side panel: render only the maneuvers in selectedEvents() (rescales X/Y to that set). Falls back to all when nothing selected. Colours are unaffected — they always come from resolveItemColor, so the panel matches the map and video tiles. */
  selectedOnly?: boolean;
  /** Side panel: draw + animate a red playhead line tracking maneuverVideoElapsedSeconds(); drag scrubs the video (VIDEO) or moves the boat (MAP). */
  playhead?: boolean;
  /** Side panel: size the chart container to its parent (internal vertical scroll) instead of the window. */
  fitParent?: boolean;
}

interface TimeSeriesDataPoint {
  event_id: number;
  tws_bin: number;
  tack: string;
  vmg_perc_avg: number;
  race: number | string;
  source_name: string;
  State?: string;
  Config?: string;
  time: number;
  [key: string]: any; // For dynamic channels
}

export default function TimeSeries(props: TimeSeriesProps) {
  // Get context from props, default to 'dataset' for backward compatibility
  const context = props?.context || 'dataset';
  const [tsdata, setTsData] = createSignal<TimeSeriesDataPoint[][]>([]);
  const [mode_x] = createSignal(true);
  const [loading, setLoading] = createSignal(true);
  const [firstload, setFirstLoad] = createSignal(true);
  const [channelOrder, setChannelOrder] = createSignal<string[]>([]);
  const [chartsFromData, setChartsFromData] = createSignal<string[]>([]);

  const isPanelActive = (): boolean => {
    const fn = props.panelActive;
    return fn ? fn() : true;
  };

  createEffect(() => {
    if (!isPanelActive()) return;
    props.onLoadStateChange?.(loading());
  });

  /** Multi-group lazy sections: no description prop (or explicit multiGroup). */
  const isMultiGroupMode = () => props.multiGroup === true || props.description === undefined;

  const [groupOptionKeys, setGroupOptionKeys] = createSignal<string[]>(["BASICS"]);
  type GroupSeriesStatus = "idle" | "loading" | "loaded" | "error";
  type GroupSeriesEntry = {
    status: GroupSeriesStatus;
    data?: TimeSeriesDataPoint[][];
    charts?: string[];
    channelOrder?: string[];
  };
  const [groupSeriesState, setGroupSeriesState] = createSignal<Record<string, GroupSeriesEntry>>({});
  let multiFetchAbort: AbortController | null = null;
  let multiSessionCounter = 0;
  const groupSectionObservers = new Map<string, IntersectionObserver>();
  const groupPlotMounts = new Map<string, HTMLDivElement | null>();
  let multiPlotsRootEl: HTMLDivElement | null = null;

  // Viewport tracking for performance optimization
  const [chartVisibility, setChartVisibility] = createSignal<Map<string, boolean>>(new Map());
  // Buffer: ~3 chart rows for IntersectionObserver preload (matches flex gap + chart height)
  const getViewportBuffer = () => {
    const chartHeightWithGap = chartHeight + 8;
    return chartHeightWithGap * 3;
  };
  let intersectionObservers = new Map<string, IntersectionObserver>();
  /** Coalesce rapid effect/onMount paths into one fetch (avoids fetch + console spam). */
  let updateTimeSeriesDebounce: ReturnType<typeof setTimeout> | null = null;
  /** Dedupe noisy debug when chart list unchanged across refetches. */
  let lastExtractedChartsLogKey = '';
  let storedMouseHandlers = new Map<string, {
    mouseover: ((event: MouseEvent, d: TimeSeriesDataPoint[]) => void) | null;
    mouseout: (() => void) | null;
    mousemove: ((event: MouseEvent, d: TimeSeriesDataPoint[]) => void) | null;
    click: ((event: MouseEvent, d: any) => void) | null;
  }>();

  let timeArray: any[][] = []
  let valArray: any[][] = []
  let eidArray: number[] = []

  let chartHeight = 200
  let chartWidth = 1280
  let containerRef: HTMLDivElement | null = null
  let plotsEl: HTMLDivElement | null = null

  const domIdPrefix = () => (props.instanceId ? `${props.instanceId}-` : "");

  let lastResizeObservedWidth = 0;
  let lastAppliedLayoutHeightPx = 0;
  let resizeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  // One-shot guard: set true right before updateContainerHeight writes the container/area heights, so the
  // ResizeObserver cycle that our own write triggers is skipped (only when width is unchanged). Without
  // this, measuring the now-taller plotsRoot feeds back into updateContainerHeight and grows it endlessly.
  let suppressSelfResizeOnce = false;

  /** VIDEO playhead: per-chart updater that repositions the red line to a given seconds value. */
  const playheadUpdaters = new Map<string, (sec: number) => void>();

  const getElementLayoutHeight = (element: HTMLElement | null, scaleFactor: number): number => {
    if (!element) return 0;
    const scale = Number.isFinite(scaleFactor) && scaleFactor > 0 ? scaleFactor : 1;
    const elementRect = element.getBoundingClientRect();
    const rectHeight = elementRect.height / scale;
    let childBottom = 0;
    Array.from(element.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      const childRect = child.getBoundingClientRect();
      childBottom = Math.max(childBottom, (childRect.bottom - elementRect.top) / scale);
    });
    return Math.max(element.scrollHeight, element.offsetHeight, rectHeight, childBottom);
  };

  const getRenderedChartStackHeight = (scaleFactor: number): number =>
    getElementLayoutHeight(multiPlotsRootEl ?? plotsEl, scaleFactor);

  // Using global color scale for consistency
  let myColorScale: d3.ScaleLinear<number, string> | d3.ScaleThreshold<number, string> | d3.ScaleOrdinal<string | number, string> = d3.scaleLinear<number, string>()
  let getItemColor: ((d: any) => string) | null = null; // Store getItemColor function for SOURCE coloring
  let minVal = 999999
  let maxVal = -999999

  const isEventSelected = (id: number) => highlightedEventIds().includes(id);

  const buildDescription = (): string =>
    buildTimeseriesDescription(phase(), String(props?.description || "BASICS").toUpperCase());


  const updateChartWidth = (): void => {
    if (containerRef) {
      const containerWidth = containerRef.offsetWidth || 1280;
      chartWidth = Math.max(containerWidth - 20, 400); // Use container width with 20px margin, min 400px
    } else {
      chartWidth = 1280; // Fallback width
    }
  }

  const updateChartHeight = (): void => {
    chartHeight = 200; // Fixed 200px height
  }

  // Async function to fetch data
  const fetchData = async (): Promise<TimeSeriesDataPoint[][]> => {
    try {
      // Get the current filtered event list - time series endpoint requires non-empty event_list
      const currentFiltered = filtered();
      // Extract event IDs from filtered data (handles both number[] and ManeuverData[])
      let eventIds: number[] = [];
      if (Array.isArray(currentFiltered) && currentFiltered.length > 0) {
        if (typeof currentFiltered[0] === 'number') {
          eventIds = currentFiltered as unknown as number[];
        } else {
          eventIds = (currentFiltered as any[]).map((item: any) => item.event_id).filter((id: any): id is number => typeof id === 'number');
        }
      }
      
      const hasEvents = eventIds.length > 0;
      
      // Time series endpoint requires event_list to be non-empty, so we must have events
      if (!hasEvents) {
        // No events yet (e.g. table still loading or no events selected) – skip fetch for now
        debug('TimeSeries: Skipping fetch - no events in filtered list');
        return [];
      }
      
      // Build description from phase and selected description
      const descriptionStr = buildDescription();

      if (eventIds.length === 0) {
        debug('TimeSeries: No valid event IDs found in filtered list');
        return [];
      }

      // Always use event_list so timeseries data matches table filtering (same event set).
      // Service chunks when >100 events to avoid URL length limits.
      const fetchParams: any = {
        eventType: eventType(),
        description: descriptionStr,
        eventList: eventIds,
      };

      debug('TimeSeries: fetchParams before calling fetchTimeSeriesData:', {
        hasEventList: !!fetchParams.eventList,
        eventListLength: fetchParams.eventList?.length || 0,
        hasTimeRange: !!fetchParams.timeRange,
        hasFilters: !!fetchParams.filters
      });

      // Fetch timeseries data
      const timeseriesResponse = await fetchTimeSeriesData(context, fetchParams);

      const parsed = parseTimeSeriesResponse({
        timeseriesResponse,
        eventIds,
        isEventHidden,
        tableRows: tabledata() as Array<Record<string, unknown>>,
        phaseVal: phase(),
      });
      const dataArray = parsed.dataArray as TimeSeriesDataPoint[][];
      if (parsed.charts.length > 0) {
        setChartsFromData(parsed.charts);
        const digest = parsed.charts.join("\0");
        if (digest !== lastExtractedChartsLogKey) {
          lastExtractedChartsLogKey = digest;
          debug("TimeSeries: charts from response:", parsed.charts);
        }
      }
      if (parsed.channelOrder.length > 0) {
        setChannelOrder(parsed.channelOrder);
      }

      if (!dataArray || dataArray.length === 0) {
        return [];
      }

      setTsData(dataArray);
      updateMinMaxRanges();
      return dataArray;
    } catch (error: any) {
      logError("Error fetching data:", error);
      return [];
    }
  };

  function updateMinMaxRanges() {
    let channel = 'tws_bin'
    if (color() == 'VMG') {
      channel = 'vmg_perc_avg'
    }

    minVal = 9999999
    maxVal = -9999999
    tsdata().forEach(function(item) {
      try {
        let d = item[0]
        let val = parseFloat(d[channel])
        if (val > maxVal) {maxVal = val}
        if (val < minVal) {minVal = val}
      } catch {
        /* ignore non-numeric values */
      }
    })

    InitScales()  
  }

  function InitScales(): void {
    const currentColor = String(color() || '').toUpperCase();
    
    if (currentColor === 'TWS') {
      // Fixed TWS bins: 10, 15, 20, 25, 30, 35, 40, 45, 50
      // Use scaleThreshold with 8 thresholds to create 9 color ranges (one for each bin)
      const twsScale = d3.scaleThreshold<number, string>()
      twsScale.domain([12.5, 17.5, 22.5, 27.5, 32.5, 37.5, 42.5, 47.5]);
      twsScale.range(["blue","lightblue","cyan","lightgreen","yellow","orange","red","darkred","purple"] as any)
      myColorScale = twsScale as any;
    }
    else if (currentColor === 'VMG') {
      const vmgScale = d3.scaleLinear<number, string>()
      vmgScale.domain([minVal, (minVal + maxVal) / 2, maxVal])
      vmgScale.range(["blue","lightgrey","red"] as any)
      myColorScale = vmgScale as any;
    }
    else if (currentColor === 'TACK') {
      const tackScale = d3.scaleThreshold<number, string>()
      tackScale.domain([-180,-1,1,180])
      tackScale.range(["red","red","#64ed64","#64ed64"] as any)
      myColorScale = tackScale as any;
    }
    else if (
      currentColor === "RACE" ||
      currentColor === "SOURCE" ||
      currentColor === "STATE" ||
      currentColor === "CONFIG" ||
      currentColor === "CREW" ||
      currentColor === "TESTS" ||
      currentColor === "YEAR" ||
      currentColor === "EVENT"
    ) {
      // Use buildColorGrouping for consistency with ManeuverScatter
      const data: TimeSeriesDataPoint[] = [];
      tsdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, currentColor);
      myColorScale = scale as any;
      getItemColor = getItemColorFn as ((d: any) => string) | null; // Store the function for use in getOriginalColor
    }
    else {
      const defaultScale = d3.scaleLinear<number, string>()
      // defaultScale.domain([4, 8, 14, 18, 22])
      defaultScale.domain([8, 16, 28, 36, 44]);
      defaultScale.range(["yellow","orange","red"] as any)
      myColorScale = defaultScale as any;
    }
  }

  function getClass(d: TimeSeriesDataPoint): string {
    if (d.tack  == 'PORT') {
        return "dash_line"
    } else if (d.tack == 'STBD') {
      return "solid_line"
    } else if (d.tack == 'P - S') {
        return "dash_line"
    } else {
        return "solid_line"
    }
  }

  // Get the original color based on current color-by setting (without selection-based coloring)
  function getOriginalColor(d: TimeSeriesDataPoint): string {
    if (!d) return 'grey';
    
    const currentColor = String(color() || '').toUpperCase();
    
    if (currentColor === 'TWS') {
      const val = d.tws_bin;
      return String((myColorScale as any)(val) || 'grey');
    }
    else if (currentColor === 'VMG') {
      const val = d.vmg_perc_avg;
      return String((myColorScale as any)(val) || 'grey');
    }
    else if (currentColor === 'TACK') {
      if (d.tack === 'PORT' || d.tack === 'P - S') {
        return '#d62728';
      } else if (d.tack === 'STBD' || d.tack === 'S - P') {
        return '#2ca02c';
      }
    }
    else if (
      currentColor === "RACE" ||
      currentColor === "SOURCE" ||
      currentColor === "STATE" ||
      currentColor === "CONFIG" ||
      currentColor === "CREW" ||
      currentColor === "TESTS" ||
      currentColor === "YEAR" ||
      currentColor === "EVENT"
    ) {
      if (getItemColor) {
        return getItemColor(d) || "grey";
      }
      return "grey";
    }
    return "grey";
  }

  function getColor(d: TimeSeriesDataPoint): string {
    return resolveItemColor(d as Record<string, unknown>, {
      selectedIds: highlightedEventIds(),
      legendSelectionActive: legendSelectionActive(),
      colorMode: color(),
      getDimensionColor: (item) => getOriginalColor(item as TimeSeriesDataPoint),
    });
  }
  
  function make_x_gridlines(xScale: d3.ScaleLinear<number, number>) {
    return d3.axisBottom(xScale)
      .ticks(10).tickFormat(myTickFormat)
  }
  
  function make_y_gridlines(yScale: d3.ScaleLinear<number, number>) {
    return d3.axisLeft(yScale)
      .ticks(5).tickFormat(myTickFormat)
  }

  function buildTimeSeries(
    data: TimeSeriesDataPoint[][],
    scoped?: {
      mountEl: HTMLDivElement;
      groupKey: string;
      chartsOrder: string[];
      channelOrderStored: string[];
    }
  ) {
    const mount = scoped?.mountEl ?? plotsEl;
    if (!mount) {
      debug("TimeSeries: plots container not ready, skip buildTimeSeries");
      return;
    }

    // VIDEO selectedOnly: drop points for maneuvers not in the current selection so the chart
    // renders (and rescales X/Y to) just the maneuvers being watched. Point-level so it is correct
    // regardless of how points are grouped into the outer arrays. Falls back to all when nothing selected.
    // Gate on highlightedEventIds() (manual ∪ live), the same set isEventSelected filters by — on the
    // live pages in follow mode the shown maneuvers come from the live highlight, not the selection.
    if (props.selectedOnly && highlightedEventIds().length > 0) {
      data = data
        .map((arr) => (Array.isArray(arr) ? arr.filter((d) => d && isEventSelected(d.event_id)) : arr))
        .filter((arr) => Array.isArray(arr) && arr.length > 0);
    }
    const idp = domIdPrefix();
    const groupKey = scoped?.groupKey ?? "";
    const idSeg = groupKey ? `${groupKey}-` : "";
    const plotsSelection = d3.select(mount);
    plotsSelection.selectAll("*").remove();

    if (scoped) {
      const prefix = `${idp}${idSeg}`;
      intersectionObservers.forEach((observer, key) => {
        if (key.startsWith(prefix)) {
          observer.disconnect();
          intersectionObservers.delete(key);
        }
      });
      setChartVisibility((prev) => {
        const next = new Map(prev);
        for (const k of next.keys()) {
          if (k.startsWith(prefix)) {
            next.delete(k);
          }
        }
        return next;
      });
    } else {
      intersectionObservers.forEach((observer) => {
        observer.disconnect();
      });
      intersectionObservers.clear();
      setChartVisibility(new Map());
    }

    // Update chart dimensions based on container size
    updateChartWidth();
    updateChartHeight();

    if (data.length > 0 && data[0].length > 0) {
      const firstPoint = data[0][0];

      // Dynamically extract channels from first data point
      // Exclude metadata fields that are not visualization channels
      const excludedFields = new Set([
        "event_id",
        "tack",
        "race",
        "source_name",
        "tws_bin",
        "time",
        "vmg_perc_avg",
        "sink_min",
        "State",
        "state",
        "STATE",
        "Config",
        "config",
        "CONFIG",
        "Crew",
        "crew",
        "CREW",
        "CREW_CODE",
        "Tests",
        "tests",
        "TESTS",
        "TEST",
      ]);

      const chartsOrder = scoped?.chartsOrder ?? chartsFromData();
      const storedOrder = scoped?.channelOrderStored ?? channelOrder();
      const ts_columns: string[] = [];
      
      const includeChartChannel = (k: string, requirePoint: boolean): boolean =>
        !excludedFields.has(k) &&
        isManeuverTimeseriesChartChannel(k) &&
        (!requirePoint || firstPoint[k] !== undefined);

      if (chartsOrder.length > 0) {
        // Use ONLY charts order from timeseries data - don't mix with stored order
        // This ensures the exact order from the timeseries data is preserved
        chartsOrder.forEach(k => {
          if (includeChartChannel(k, true)) {
            ts_columns.push(k);
          }
        });
      } else if (storedOrder.length > 0) {
        // Fallback: Use stored order from JSON
        storedOrder.forEach(k => {
          if (includeChartChannel(k, true)) {
            ts_columns.push(k);
          }
        });
      } else {
        // Final fallback: extract from first point if channel order not available
        Object.keys(firstPoint).forEach(k => {
          if (includeChartChannel(k, false)) {
            ts_columns.push(k);
          }
        });
      }

      timeArray = []
      valArray = []
      eidArray = []

      for (let c = 1; c < ts_columns.length + 1; c++) {
        const channel = ts_columns[c - 1];
        const channelSlug = String(channel).replace(/[^a-zA-Z0-9_-]/g, "_");
        const chartId = `${idp}${idSeg}timeseries-chart-${channelSlug}`;
        const svgPlotId = `${idp}${idSeg}ts${c}`;

        // Create a wrapper div for each chart with fixed width and unique ID
        const chartWrapper = plotsSelection
          .append("div")
          .attr("class", "timeseries-chart-wrapper")
          .attr("id", chartId)
          .attr("data-channel", channel)
          .attr("data-chart-search-title", String(channel).toUpperCase())
          .attr("data-chart-search-detail", groupKey)
          .style("width", `${chartWidth}px`) // Fixed width for each chart
          .style("flex-shrink", "0"); // Spacing via .maneuver-time-series flex gap (Styles.css)

        // Create SVG container (will be populated when visible)
        chartWrapper.append("svg")
          .attr("id", svgPlotId)
          .attr("class", "ts")
          .attr("width", chartWidth)
          .attr("height", chartHeight);

        // Set up IntersectionObserver for this chart
        const chartElement = document.getElementById(chartId);
        if (chartElement) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach(entry => {
                const currentVisibility = chartVisibility();
                const wasVisible = currentVisibility.get(chartId) || false;
                const isVisible = entry.isIntersecting;

                // Manual viewport check to verify actual visibility
                const rect = chartElement.getBoundingClientRect();
                const isActuallyVisible = rect && rect.top < window.innerHeight && rect.bottom > 0 && rect.width > 0 && rect.height > 0;

                // Avoid signal churn on every IO callback when intersection state did not change
                if (wasVisible !== isVisible) {
                  const newVisibility = new Map(currentVisibility);
                  newVisibility.set(chartId, isVisible);
                  setChartVisibility(newVisibility);
                }

                // Key fix: Render if EITHER IntersectionObserver says visible OR manual check says visible
                // This handles cases where observer hasn't fired yet or missed the chart
                if (isVisible || isActuallyVisible) {
                  const plot = d3.select(mount).select(`#${escapeSelectorId(svgPlotId)}`);
                  const bodyId = `${idp}${idSeg}body${c}`;
                  const hasBody = !plot.select(`#${escapeSelectorId(bodyId)}`).empty();
                  
                  if (!hasBody) {
                    // Chart needs to be rendered
                    plot.append("g")
                      .attr("id", bodyId)
                      .attr("transform", "translate(20, 5)");
                    const body = plot.select(`#${escapeSelectorId(bodyId)}`);
                    drawTimeSeriesOffThread(c - 1, body, data, channel, chartId);
                  }
                } else if (!isVisible && wasVisible && !isActuallyVisible) {
                  // Chart left viewport - only remove if actually not visible (double-check)
                  const plot = d3.select(mount).select(`#${escapeSelectorId(svgPlotId)}`);
                  plot.selectAll("*").remove();
                  // Remove stored handlers for this chart
                  storedMouseHandlers.delete(chartId);
                }
              });
            },
            {
              // Buffer: 3 charts worth of space (preload before visible and keep loaded after leaving viewport)
              rootMargin: `${getViewportBuffer()}px 0px ${getViewportBuffer()}px 0px`,
              threshold: 0.01
            }
          );
          
          observer.observe(chartElement);
          intersectionObservers.set(chartId, observer);

          // Check initial visibility and render if already visible
          // Use requestAnimationFrame for better timing with DOM updates
          requestAnimationFrame(() => {
            const rect = chartElement.getBoundingClientRect();
            const isInitiallyVisible = rect && rect.top < window.innerHeight && rect.bottom > 0 && rect.width > 0 && rect.height > 0;
            if (isInitiallyVisible) {
              const bodyId = `${idp}${idSeg}body${c}`;
              const plot = d3.select(mount).select(`#${escapeSelectorId(svgPlotId)}`);
              const hasBody = !plot.select(`#${escapeSelectorId(bodyId)}`).empty();
              if (!hasBody) {
                // Set visibility BEFORE drawTimeSeries so tooltip hover circles are added on first paint
                const newVisibility = new Map(chartVisibility());
                newVisibility.set(chartId, true);
                setChartVisibility(newVisibility);
                plot.append("g")
                  .attr("id", bodyId)
                  .attr("transform", "translate(20, 5)");
                const body = plot.select(`#${escapeSelectorId(bodyId)}`);
                drawTimeSeriesOffThread(c - 1, body, data, channel, chartId);
              }
            }
          });
        }
      }

      // Add a fake chart at the end to ensure last chart is fully visible when scrolling
      // This matches the structure of real charts to improve scroll behavior
      const fakeChartWrapper = plotsSelection
        .append("div")
        .attr("class", "timeseries-chart-wrapper timeseries-chart-spacer")
        .style("width", `${chartWidth}px`)
        .style("flex-shrink", "0")
        .style("opacity", "0"); // Invisible tail for scroll / last-chart clearance

      fakeChartWrapper.append("svg")
        .attr("id", `${idp}${idSeg}ts-spacer`)
        .attr("class", "ts")
        .attr("width", chartWidth)
        .attr("height", 24);

      requestAnimationFrame(() => {
        updateContainerHeight();
      });
      
      // Ensure selection colors are applied after all charts are drawn
      // This is important when switching from Scatter to TimeSeries with existing selections
      // Also ensures synchronization when opening ManeuverWindow - always call updateSelection
      // to sync with current selection state from selectionStore (even if empty)
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        updateSelection();
      }, 0);
    }
  }

  function getToolTipPosition(event) {
    try {
      if (!containerRef) return { x: 0, y: 0 };
      
      // Use fixed positioning relative to viewport
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      
      const pad = 12;
      const estWidth = 220;
      const estHeight = 140;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Find the scroll container (could be body, html, or main-content)
      let scrollContainer: HTMLElement | null = null;
      const mainContent = document.getElementById('main-content');
      if (mainContent && mainContent.scrollHeight > mainContent.clientHeight) {
        scrollContainer = mainContent;
      } else if (document.body.scrollHeight > document.body.clientHeight) {
        scrollContainer = document.body;
      } else if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
        scrollContainer = document.documentElement;
      }
      
      // Get scroll position to understand where we are on the page
      const scrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY || 0;
      const scrollX = scrollContainer ? scrollContainer.scrollLeft : window.scrollX || 0;
      
      // Calculate tooltip position relative to viewport
      // Position to the right of cursor by default
      let x = mouseX + pad;
      // If tooltip would go off right edge, position to the left of cursor
      if (x + estWidth > viewportWidth) {
        x = Math.max(pad, mouseX - estWidth - pad);
      }
      
      // Position above cursor by default (with small offset)
      let y = mouseY - estHeight - pad;
      
      // If tooltip would go off top of viewport, position below cursor
      if (y < pad) {
        y = mouseY + pad;
        // If still off bottom, position above cursor but within viewport
        if (y + estHeight > viewportHeight) {
          y = Math.max(pad, viewportHeight - estHeight - pad);
        }
      } else if (y + estHeight > viewportHeight) {
        // If tooltip would go off bottom, position above cursor
        y = Math.max(pad, mouseY - estHeight - pad);
      }

      // Translate tooltip 75px to the left of computed position
      x -= 75;

      return { x, y };
    } catch (error) {
      logError('Error calculating tooltip position:', error);
      return { x: 0, y: 0 };
    }
  }

  interface ManeuverPathType {
    eventId: number;
    points: TimeSeriesDataPoint[];
    firstPoint: TimeSeriesDataPoint;
  }

  // Build this chart's maneuver paths on a @nodets worker pool, then draw. Falls back to the inline
  // computation inside drawTimeSeries if the pool rejects. Charts draw once as they scroll into view,
  // so several can crunch concurrently across cores instead of blocking the main thread on each paint.
  function drawTimeSeriesOffThread(
    index: number,
    chartbody: d3.Selection<d3.BaseType, unknown, null, undefined>,
    collection: TimeSeriesDataPoint[][],
    channel: string,
    chartWrapperId: string
  ) {
    computeManeuverPathsOffThread(collection as any, channel)
      .then((paths) => drawTimeSeries(index, chartbody, collection, channel, chartWrapperId, paths as unknown as ManeuverPathType[]))
      .catch(() => drawTimeSeries(index, chartbody, collection, channel, chartWrapperId));
  }

  function drawTimeSeries(
    index: number,
    chartbody: d3.Selection<d3.BaseType, unknown, null, undefined>,
    collection: TimeSeriesDataPoint[][],
    channel: string,
    chartWrapperId: string,
    precomputedPaths?: ManeuverPathType[]
  ) {
    const currentVisibility = chartVisibility();
    const isChartVisible = currentVisibility.get(chartWrapperId) || false;
    
    // Skip rendering if chart is not visible (unless it's the initial render)
    if (!isChartVisible && chartbody.selectAll("*").size() > 0) {
      return;
    }
    
    let minXValue = 999999
    let maxXValue = -999999
    
    let minYValue = 999999
    let maxYValue = -999999

    let maneuverPathsFiltered: ManeuverPathType[] = [];
    // Declared at the top of the function (not beside `idled`/`updateChart` below) because the hoisted
    // `updateChart` is called earlier in this function than its own definition; a `let` beside the
    // definition would be in its temporal dead zone at that call (the former `var` was hoisted).
    let idleTimeout: ReturnType<typeof setTimeout> | null = null;
  
    if (collection != undefined) {
      // Ensure arrays exist for this index
      if (!timeArray[index]) {
        timeArray[index] = [];
      }
      if (!valArray[index]) {
        valArray[index] = [];
      }
      
      // Cache mode_x() value to avoid repeated function calls
      const useTimeForX = mode_x();
      const fill_eid = eidArray.length === 0;
      
      // Pre-allocate arrays for better performance
      const timeArr = timeArray[index];
      const valArr = valArray[index];
      
      // Safety check: ensure arrays are defined
      if (!timeArr || !valArr) {
        logError(`Arrays not initialized for index ${index}`);
        return;
      }

      // Group samples by event and sort by time. This runs on a @nodets worker pool when the caller passes
      // `precomputedPaths`; otherwise it computes inline via the identical shared builder
      // (buildManeuverPaths) — same result either way.
      maneuverPathsFiltered =
        (precomputedPaths ?? buildManeuverPaths({ collection: collection as any, channel })) as unknown as ManeuverPathType[];

      // Process data: X range and arrays from full collection; Y range from outlier-adjusted paths
      for (let i = 0; i < collection.length; i++) {
        const vals = collection[i];
        for (let j = 0; j < vals.length; j++) {
          const d = vals[j];
          
          const bspVal = (d as any).bsp || 0;
          const xVal = useTimeForX ? parseFloat(String(d.time)) : parseFloat(String(bspVal));
          if (xVal > maxXValue) maxXValue = xVal;
          if (xVal < minXValue) minXValue = xVal;

          const yVal = (d as any)[channel];
          const yValNum = typeof yVal === 'number' ? yVal : parseFloat(String(yVal || 0));

          timeArr.push(useTimeForX ? d.time : bspVal);
          valArr.push(yValNum);

          if (fill_eid) {
            eidArray.push(d.event_id);
          }
        }
      }

      // Y scale from outlier-adjusted data when available; otherwise fall back to full collection
      // Include zero so channels that cross zero (e.g. TWA_N_DEG) are fully visible
      if (maneuverPathsFiltered.length > 0) {
        minYValue = 999999;
        maxYValue = -999999;
        maneuverPathsFiltered.forEach((m) => {
          m.points.forEach((d) => {
            const yValNum = Number((d as any)[channel]);
            if (Number.isFinite(yValNum)) {
              if (yValNum > maxYValue) maxYValue = yValNum;
              if (yValNum < minYValue) minYValue = yValNum;
            }
          });
        });
      } else {
        // No filtered paths: use Y range from collection (same as timeArr/valArr loop above)
        for (let i = 0; i < collection.length; i++) {
          const vals = collection[i];
          for (let j = 0; j < vals.length; j++) {
            const yValNum = Number((vals[j] as any)[channel]);
            if (Number.isFinite(yValNum)) {
              if (yValNum > maxYValue) maxYValue = yValNum;
              if (yValNum < minYValue) minYValue = yValNum;
            }
          }
        }
      }
    }

    // The reference line is drawn after this scale. Fold its values in first, or a baseline above
    // or below the runs is clipped off the chart.
    if (maneuverBaselineStore.baselineActive() && !maneuverBaselineStore.baselineLoading()) {
      const baselineY = baselineTimeseriesChannelExtent(maneuverBaselineStore.baselineTracks(), channel);
      if (baselineY) {
        if (minYValue === 999999 || maxYValue === -999999) {
          minYValue = baselineY.min;
          maxYValue = baselineY.max;
        } else {
          if (baselineY.min < minYValue) minYValue = baselineY.min;
          if (baselineY.max > maxYValue) maxYValue = baselineY.max;
        }
      }
    }
  
    maxYValue = maxYValue + ((maxYValue - minYValue) / 5)
    minYValue = minYValue - ((maxYValue - minYValue) / 15)
  
    if (maxYValue - minYValue < 1.5) {
      if (minYValue < 0.25 && minYValue > 0) {
        minYValue = -0.1
      }
    }
  
    //SETUP SCALES
    let yScale = d3.scaleLinear()
      .domain([minYValue, maxYValue])
      .range([chartHeight, 0]);
      
    const darkMode = isDark();

    let yAxis = chartbody.append("g")
      .attr("class", "axes")
      .attr("transform", "translate(20, 0)")
      .call(d3.axisLeft(yScale).ticks(4).tickFormat((d: d3.NumberValue) => myTickFormat(Number(d))));

    // Style y-axis tick labels based on theme
    yAxis.selectAll("text")
      .style("fill", darkMode ? "#ffffff" : "#000000");
    
    let xScale = d3.scaleLinear()
      .domain([minXValue, maxXValue])
      .range([0, chartWidth]);
    
    let xAxis = chartbody.append("g")
      .attr("class", "axes")
      .attr("transform", "translate(20, " + chartHeight + ")")
      .call(d3.axisBottom(xScale).ticks(10).tickFormat((d: d3.NumberValue) => String(Number(d))))
    
    chartbody.append("text")
      .attr("class", "axis-label")
      .attr("text-anchor", "top")  
      .attr("transform", "translate(30,15)")  
      .attr("font-size", "14px")
      .attr("user-select", "none")
      .attr("pointer-events", "none")
      .attr("fill", darkMode ? "#ffffff" : "#000000")
      .text(channel.toUpperCase()) 
  
    chartbody.append("g")
      .attr("class","grid")
      .attr("transform", "translate(20," + chartHeight + ")")
      .style("stroke-dasharray",("3,3"))
      .call(make_x_gridlines(xScale)
        .tickSize(-chartHeight)
        .tickFormat("")
      )
  
    chartbody.append("g")
      .attr("class","grid")
      .attr("transform", "translate(20, 0)")
      .style("stroke-dasharray",("3,3"))
      .call(make_y_gridlines(yScale)
        .tickSize(-chartWidth)
        .tickFormat("")
      )
  
    const brush = d3.brush()
      .extent( [ [0,0], [chartWidth,chartHeight] ] )
      .on("end", updateChart) 
  
    chartbody.append("g")
      .attr("class", "brush")
      .call(brush)
      .on("dblclick", refreshChart);

    // Mouse event handlers
    const mouseover = (event: MouseEvent, d: TimeSeriesDataPoint[]) => {
      const tooltipContent = getTooltipContent(d[0]); 
      const position = getToolTipPosition(event);

      setTooltip({
          visible: true,
          content: tooltipContent,
          x: position.x - 150,
          y: position.y
      });
    };

    const mousemove = (event: MouseEvent, d: TimeSeriesDataPoint[]) => {
      const tooltipContent = getTooltipContent(d[0]); 
      const position = getToolTipPosition(event);

      setTooltip({
          visible: true,
          content: tooltipContent,
          x: position.x - 150,
          y: position.y
      });
    };

    const mouseout = () => {
      setTooltip({
        visible: false,
        content: "",
        x: 0,
        y: 0
      });
    };

    const getTooltipContent = (point: TimeSeriesDataPoint): string => {
      if (!point) return "";

      // Join with table data by event_id; datetime comes pre-formatted in local time (datetimeLocal)
      const tableData = tabledata();
      const tableRow = tableData.find((row: any) => row.event_id === point.event_id);
      let dateString = "N/A";
      let timeString = "N/A";
      if (tableRow?.datetimeLocal) {
        const parts = tableRow.datetimeLocal.split(" ");
        dateString = parts[0] ?? "N/A";
        timeString = parts[1] ?? "N/A";
      }

      const isFleet = point.source_name != null && String(point.source_name).trim() !== "";
      const useTimeForX = mode_x();
      const xVal = useTimeForX ? point.time : ((point as any).bsp ?? 0);
      // X is always displayed as seconds (float), not formatted as date/time
      const xDisplay = typeof xVal === "number" && !isNaN(xVal)
        ? String(parseFloat(xVal.toFixed(2)))
        : String(xVal);
      const yVal = (point as any)[channel];
      const yNum = typeof yVal === "number" ? yVal : parseFloat(String(yVal || 0));
      const yDisplay = !isNaN(yNum) ? (Math.round(yNum * 10) / 10).toFixed(1) : String(yVal ?? "");

      let tooltipRows = `
              <tr><td>DATE</td><td>${dateString}</td></tr>
              <tr><td>TIME</td><td>${timeString}</td></tr>`;
      if (isFleet) {
        tooltipRows += `
              <tr><td>SOURCE</td><td>${String(point.source_name)}</td></tr>`;
      }
      tooltipRows += `
              <tr><td>TIME (SEC)</td><td>${xDisplay}</td></tr>
              <tr><td>${String(channel || "").toUpperCase()}</td><td>${yDisplay}</td></tr>`;

      return `<table class='table-striped'>${tooltipRows}</table>`;
    };
    
    const click = function(event: MouseEvent, d: { eventId: number; firstPoint: TimeSeriesDataPoint; points: TimeSeriesDataPoint[] } | TimeSeriesDataPoint[] | TimeSeriesDataPoint) {
      event.stopPropagation();
      
      let id: number | undefined;
      if (d && typeof d === 'object' && 'eventId' in d) {
        id = (d as { eventId?: number }).eventId;
      } else if (d && typeof d === 'object' && 'firstPoint' in d && d.firstPoint && d.firstPoint.event_id !== undefined) {
        id = d.firstPoint.event_id;
      } else if (Array.isArray(d) && d[0] && d[0].event_id !== undefined) {
        id = d[0].event_id;
      } else if (d && typeof d === 'object' && 'event_id' in d) {
        id = (d as TimeSeriesDataPoint).event_id;
      }
      
      if (id === undefined || id === null) {
        return;
      }
      
      setLegendSelectionActive(false);
      let newSelectedEvents: number[] = [];
      setSelectedEvents((prev) => {
        if (prev.includes(id)) {
          newSelectedEvents = prev.filter((e) => e !== id);
        } else {
          newSelectedEvents = [...prev, id];
        }
        return newSelectedEvents;
      });

      setTriggerSelection(true)

      // Use the new state to determine hasSelection
      if (newSelectedEvents.length > 0) {
        setHasSelection(true);
      } else {
        // Don't clear hasSelection when the other panel has a brush selection (e.g. map timeseries brushed in split view)
        const hasBrushRange = selectedRange() && selectedRange().length > 0;
        if (!hasBrushRange) {
          setHasSelection(false);
          setSelection([]);
        }
      }
    };

    // DRAW CONTENT
    // Use curveCatmullRom for smoother interpolation - provides very smooth curves
    // Alpha of 0.5 gives a good balance between smoothness and following the data points
    const toLinePath = d3.line<TimeSeriesDataPoint>()
      .x(d => (mode_x() ? xScale(d.time) : xScale((d as any).bsp || 0)))
      .y(d => yScale((d as any)[channel] || 0))
      .curve(d3.curveCatmullRom.alpha(0.5)); // Smooth Catmull-Rom spline with moderate alpha
    
    /**
     * ONE baseline line per chart, both tacks together — the stored per-maneuver timeseries is already
     * side-normalized by the pipeline, so port and starboard traces are directly averageable.
     *
     * Draws nothing when no averaged trace covers this chart's channel: a channel the member payload
     * does not carry has no reference, and a flat line at zero would invent one.
     */
    function drawBaselineTimeseriesLine(): void {
      chartbody.selectAll(".linePath-baseline").remove();
      if (!maneuverBaselineStore.baselineActive()) { return; }
      if (maneuverBaselineStore.baselineLoading()) { return; }
      const tracks = maneuverBaselineStore.baselineTracks();
      if (!tracks) { return; }

      const points = baselineTimeseriesForChannel(tracks, channel);
      if (points.length < 2) { return; }

      const validPoints = points
        .map((p) => {
          const val = readBaselineTimeseriesValue(p, channel);
          if (val === null) { return null; }
          return { ...p, time: p.time, [channel]: val } as TimeSeriesDataPoint;
        })
        .filter((p): p is TimeSeriesDataPoint => p !== null);
      if (validPoints.length < 2) { return; }

      chartbody
        .append("path")
        .attr("class", "linePath-baseline")
        .style("stroke", maneuverBaselineStore.baselineColor())
        .style("fill", "none")
        .attr("transform", "translate(20, 0)")
        .attr("d", toLinePath(validPoints));
    }

    function drawPaths() {
      chartbody.selectAll(".linePath").attr("d", d => {
        // Handle new format: d is a maneuver object with points array
        if (d && d.points && Array.isArray(d.points)) {
          return toLinePath(d.points);
        }
        // Fallback for old format (shouldn't happen but just in case)
        return toLinePath(d);
      });
    }

    if (collection !== undefined) {
      chartbody.select(".brush").call(brush.move, null);
      chartbody.selectAll(".linePath").remove();
      chartbody.selectAll(".linePath-outline").remove();
      chartbody.selectAll(".hover-circle").remove();
  
      const selectedPaths: ManeuverPathType[] = [];
      const nonSelectedPaths: ManeuverPathType[] = [];
      maneuverPathsFiltered.forEach((maneuver: ManeuverPathType) => {
        if (isEventSelected(maneuver.eventId)) {
          selectedPaths.push(maneuver);
        } else {
          nonSelectedPaths.push(maneuver);
        }
      });
      const orderedPaths = [...nonSelectedPaths, ...selectedPaths];
      
      const linePathsSelection = chartbody.selectAll(".linePath")
        .data(orderedPaths, (d: ManeuverPathType) => `${d.eventId}-${d.firstPoint.time}`);
  
      const selected = highlightedEventIds();
      const hasSelection = selected.length > 0;

      linePathsSelection
        .enter()
        .append("path")
        .attr("class", (d: ManeuverPathType) => `linePath ${getClass(d.firstPoint)}`)
        .merge(linePathsSelection)
        .each(function(d: ManeuverPathType) {
          const node = this as SVGPathElement;
          const isSelected = hasSelection ? isEventSelected(d.eventId) : false;

          // getColor() -> resolveItemColor() already picks the index palette for a direct
          // multi-select and the dimension colour in SOURCE mode / legend selection, and the
          // maneuver map + video tile outlines resolve colours the same way. Don't override it here
          // or the panel disagrees with the map (notably fleet SOURCE colouring).
          const strokeColor = !hasSelection || isSelected
            ? getColor(d.firstPoint)
            : "lightgrey";

          // Set stroke width
          const strokeWidth = !hasSelection ? 1 : (isSelected ? 3 : 0.5);
          
          // Set stroke opacity with !important and no transition for instant updates
          const strokeOpacity = !hasSelection ? 1 : (isSelected ? 1 : 0.5);
          
          node.style.setProperty("transition", "none", "important");
          node.style.setProperty("stroke", strokeColor, "important");
          node.style.setProperty("stroke-width", String(strokeWidth), "important");
          node.style.setProperty("stroke-opacity", String(strokeOpacity), "important");
        })
        .style("fill", "none")
        .style("pointer-events", "none") // Disable pointer events on path, circles will handle it
        .attr("transform", "translate(20, 0)")
        .attr("d", (d: ManeuverPathType) => toLinePath(d.points));

      drawBaselineTimeseriesLine();

      // Remove any existing outlines (we don't want outlines for selected paths)
      chartbody.selectAll(".linePath-outline").remove();

      // Add invisible circles for hover detection and tooltips
      // Only attach mouse events if chart is visible
      chartbody.selectAll(".hover-circle").remove();
      if (isChartVisible) {
        orderedPaths.forEach((maneuver: ManeuverPathType) => {
          const eventKey = String(maneuver.eventId).replace(/[^a-zA-Z0-9_-]/g, "_");

          const circles = chartbody.selectAll(`.hover-circle-${eventKey}`)
            .data(maneuver.points);
          
          circles.enter()
            .append("circle")
            .attr("class", `hover-circle hover-circle-${eventKey}`)
            .merge(circles)
            .attr("cx", (d: TimeSeriesDataPoint) => (mode_x() ? xScale(d.time) : xScale((d as any).bsp || 0)) + 20) // Add 20 for transform offset
            .attr("cy", (d: TimeSeriesDataPoint) => yScale((d as any)[channel] || 0))
            .attr("r", 5) // Hover detection radius
            .style("fill", "transparent")
            .style("stroke", "none")
            .style("pointer-events", "all")
            .style("cursor", "pointer")
            .on("mouseover", (event: MouseEvent, d: TimeSeriesDataPoint) => mouseover(event, [d]))
            .on("mouseout", mouseout)
            .on("mousemove", (event: MouseEvent, d: TimeSeriesDataPoint) => mousemove(event, [d]))
            .on("click", (event: MouseEvent) => click(event, maneuver));
          
          circles.exit().remove();
        });
        
        // Store mouse handlers for this chart so they can be reattached when chart becomes visible again
        storedMouseHandlers.set(chartWrapperId, {
          mouseover: mouseover,
          mouseout: mouseout,
          mousemove: mousemove,
          click: click
        });
      } else {
        // Chart not visible - disable pointer events on paths
        chartbody.selectAll(".linePath")
          .style("pointer-events", "none");
      }
  
      linePathsSelection.exit().remove();

      // VIDEO playhead: red vertical line tracking video playback (maneuverVideoElapsedSeconds()),
      // with a value dot + label per selected maneuver at the playhead time, and a draggable hit
      // area that scrubs the video. One line per chart body; positioned with this chart's xScale
      // (same +20 offset as hover circles). Only drawn when props.playhead.
      chartbody.selectAll(".maneuver-ts-playhead, .maneuver-ts-playhead-handle, .maneuver-ts-playhead-marker, .maneuver-ts-playhead-label").remove();
      if (props.playhead && mode_x()) {
        const playheadPaths = orderedPaths;
        const playheadLine = chartbody
          .append("line")
          .attr("class", "maneuver-ts-playhead")
          .attr("y1", 0)
          .attr("y2", chartHeight)
          .attr("stroke", "#e11")
          .attr("stroke-width", 2)
          .style("pointer-events", "none")
          .style("display", "none");
        // Wider transparent line on top acts as the drag handle for scrubbing.
        const playheadHandle = chartbody
          .append("line")
          .attr("class", "maneuver-ts-playhead-handle")
          .attr("y1", 0)
          .attr("y2", chartHeight)
          .attr("stroke", "transparent")
          .attr("stroke-width", 12)
          .style("cursor", "ew-resize")
          .style("display", "none");

        const valueAtSec = (points: TimeSeriesDataPoint[], sec: number): number | null => {
          if (!points || points.length === 0) { return null; }
          let closest = points[0];
          let best = Math.abs((closest.time ?? 0) - sec);
          for (const p of points) {
            const dist = Math.abs((p.time ?? 0) - sec);
            if (dist < best) { best = dist; closest = p; }
          }
          const v = Number((closest as Record<string, unknown>)[channel]);
          return Number.isFinite(v) ? v : null;
        };

        const updatePlayhead = (sec: number) => {
          chartbody.selectAll(".maneuver-ts-playhead-marker, .maneuver-ts-playhead-label").remove();
          const [d0, d1] = xScale.domain();
          if (sec == null || !Number.isFinite(sec) || sec < d0 || sec > d1) {
            playheadLine.style("display", "none");
            playheadHandle.style("display", "none");
            return;
          }
          const xPos = xScale(sec) + 20; // match hover-circle / path transform offset
          playheadLine.attr("x1", xPos).attr("x2", xPos).style("display", null);
          playheadHandle.attr("x1", xPos).attr("x2", xPos).style("display", null);
          // Value dot + label per selected maneuver at the playhead time.
          for (const path of playheadPaths) {
            const val = valueAtSec(path.points, sec);
            if (val == null) { continue; }
            const yPos = yScale(val);
            if (yPos < 0 || yPos > chartHeight) { continue; }
            // Match the line colour — same resolver, so this follows source colouring too.
            const stroke = getColor(path.firstPoint);
            chartbody.append("circle")
              .attr("class", "maneuver-ts-playhead-marker")
              .attr("cx", xPos)
              .attr("cy", yPos)
              .attr("r", 4)
              .attr("fill", stroke)
              .attr("stroke", "white")
              .attr("stroke-width", 1)
              .style("pointer-events", "none");
            chartbody.append("text")
              .attr("class", "maneuver-ts-playhead-label")
              .attr("x", xPos + 6)
              .attr("y", yPos - 10)
              .attr("font-size", "12px")
              .attr("text-anchor", "start")
              .attr("fill", stroke)
              .style("pointer-events", "none")
              .style("user-select", "none")
              .text(myTickFormat(val));
          }
        };
        // Drag to scrub: map pointer x (chartbody coords) → seconds and request a video seek.
        const dragContainer = chartbody.node() as unknown as SVGGElement;
        const drag = d3.drag<SVGLineElement, unknown>()
          .container(dragContainer)
          .on("start drag", (event) => {
            const [dd0, dd1] = xScale.domain();
            let sec = xScale.invert(event.x - 20);
            sec = Math.max(dd0, Math.min(dd1, sec));
            updatePlayhead(sec);
            // ManeuverPlayPause only exists in VIDEO mode, so don't rely on it to publish the
            // position: write the clock here too, so sibling charts' playheads follow and the
            // maneuver map can place a boat in MAP mode.
            setManeuverVideoElapsedSeconds(sec);
            setManeuverPlayheadSeconds(sec);
            requestManeuverVideoSeek(sec);
          });
        // d3's Selection<BaseType> here doesn't line up with DragBehavior<SVGLineElement>; the
        // element genuinely is the <line> we just appended, so bridge the two via unknown.
        playheadHandle.call(drag as unknown as (selection: typeof playheadHandle) => void);

        playheadUpdaters.set(chartWrapperId, updatePlayhead);
        updatePlayhead(maneuverVideoElapsedSeconds());
      } else {
        playheadUpdaters.delete(chartWrapperId);
      }
    }

    function idled() { idleTimeout = null; }

    function updateChart(event) {
      const extent = event.selection; // Brush selection
  
      if (!extent) {
        if (!idleTimeout) {
            idleTimeout = setTimeout(idled, 150); // Idle timeout logic
        }

        // Reset scales to the full domain
        xScale.domain([minXValue, maxXValue]);
        yScale.domain([minYValue, maxYValue]);

        // Update axes
        xAxis.transition().duration(1000).call(d3.axisBottom(xScale).ticks(10));
        yAxis.transition().duration(1000).call(d3.axisLeft(yScale).ticks(4));

        setTooltip({
          visible: false,
          content: "",
          x: 0,
          y: 0
        });
      } else {
        const [[x0, y0], [x1, y1]] = extent;
        const minx = xScale.invert(x0);
        const maxx = xScale.invert(x1);
        const maxy = yScale.invert(y0);
        const miny = yScale.invert(y1);

        xScale.domain([minx, maxx]);
        yScale.domain([miny, maxy]);

        interface ManeuverPathBrush {
          eventId: number;
          points: TimeSeriesDataPoint[];
          firstPoint: TimeSeriesDataPoint;
        }
        const maneuversByEvent = new Map<number, TimeSeriesDataPoint[]>();
        collection.forEach(vals => {
          vals.forEach(d => {
            let inExtent = false;
            if (mode_x()) {
              inExtent = d.time > minx && d.time < maxx && (d as any)[channel] > miny && (d as any)[channel] < maxy;
            } else {
              inExtent = (d as any).bsp > minx && (d as any).bsp < maxx && (d as any)[channel] > miny && (d as any)[channel] < maxy;
            }
            if (inExtent) {
              const eventId = d.event_id;
              if (!maneuversByEvent.has(eventId)) {
                maneuversByEvent.set(eventId, []);
              }
              maneuversByEvent.get(eventId)!.push(d);
            }
          });
        });
        const maneuverPaths: ManeuverPathBrush[] = [];
        maneuversByEvent.forEach((points, eventId) => {
          points.sort((a: TimeSeriesDataPoint, b: TimeSeriesDataPoint) => a.time - b.time);
          if (points.length > 1 && eventId !== null && eventId !== undefined) {
            maneuverPaths.push({ eventId, points: [...points], firstPoint: points[0] });
          }
        });

        if (maneuverPaths.length > 0) {
          chartbody.select(".brush").call(brush.move, null);
          chartbody.selectAll(".linePath").remove();
          chartbody.selectAll(".linePath-outline").remove();
          chartbody.selectAll(".hover-circle").remove();

          const allXValues = maneuverPaths.flatMap(m => m.points.map(d => mode_x() ? d.time : ((d as any).bsp || 0)));
          const allYValues = maneuverPaths.flatMap(m => m.points.map(d => (d as any)[channel] || 0));
          const newMinX = d3.min(allXValues);
          const newMaxX = d3.max(allXValues);
          const newMinY = d3.min(allYValues);
          const newMaxY = d3.max(allYValues);
          xScale.domain([newMinX, newMaxX]);
          yScale.domain([newMinY, newMaxY]);
          xAxis.transition().duration(1000).call(d3.axisBottom(xScale).ticks(10));
          yAxis.transition().duration(1000).call(d3.axisLeft(yScale).ticks(4));

          const selectedPaths: ManeuverPathBrush[] = [];
          const nonSelectedPaths: ManeuverPathBrush[] = [];
          maneuverPaths.forEach(maneuver => {
            if (isEventSelected(maneuver.eventId)) {
              selectedPaths.push(maneuver);
            } else {
              nonSelectedPaths.push(maneuver);
            }
          });
          const orderedPaths = [...nonSelectedPaths, ...selectedPaths];
          const linePathsSelection = chartbody.selectAll(".linePath")
              .data(orderedPaths, (d: ManeuverPathBrush) => `${d.eventId}-${d.firstPoint.time}`);
          const hasSelection = highlightedEventIds().length > 0;
          
          linePathsSelection
              .enter()
              .append("path")
              .attr("class", (d: ManeuverPathBrush) => `linePath ${getClass(d.firstPoint)}`)
              .merge(linePathsSelection)
              .each(function(d: ManeuverPathBrush) {
                const node = this as SVGPathElement;
                const isSelected = hasSelection ? isEventSelected(d.eventId) : false;
                const isColoredBySource = color() === 'SOURCE';
                let strokeColor: string;
                if (!hasSelection) {
                  strokeColor = getColor(d.firstPoint);
                } else if (isColoredBySource) {
                  strokeColor = isSelected ? getOriginalColor(d.firstPoint) : "lightgrey";
                } else {
                  strokeColor = isSelected ? getColor(d.firstPoint) : "lightgrey";
                }
                const strokeWidth = !hasSelection ? 1 : (isSelected ? 3 : 0.5);
                const strokeOpacity = !hasSelection ? 1 : (isSelected ? 1 : 0.5);
                node.style.setProperty("transition", "none", "important");
                node.style.setProperty("stroke", strokeColor, "important");
                node.style.setProperty("stroke-width", String(strokeWidth), "important");
                node.style.setProperty("stroke-opacity", String(strokeOpacity), "important");
              })
              .style("fill", "none")
              .style("pointer-events", "none")
              .attr("transform", "translate(20, 0)")
              .attr("d", (d: ManeuverPathBrush) => toLinePath(d.points));
          chartbody.selectAll(".linePath-outline").remove();
          chartbody.selectAll(".hover-circle").remove();
          orderedPaths.forEach((maneuver: ManeuverPathBrush) => {
            const eventKey = String(maneuver.eventId).replace(/[^a-zA-Z0-9_-]/g, "_");
            const circles = chartbody.selectAll(`.hover-circle-${eventKey}`)
              .data(maneuver.points);
            circles.enter()
              .append("circle")
              .attr("class", `hover-circle hover-circle-${eventKey}`)
              .merge(circles)
              .attr("cx", (d: TimeSeriesDataPoint) => (mode_x() ? xScale(d.time) : xScale((d as any).bsp || 0)) + 20)
              .attr("cy", (d: TimeSeriesDataPoint) => yScale((d as any)[channel] || 0))
              .attr("r", 5)
              .style("fill", "transparent")
              .style("stroke", "none")
              .style("pointer-events", "all")
              .style("cursor", "pointer")
              .on("mouseover", (event: MouseEvent, d: TimeSeriesDataPoint) => mouseover(event, [d]))
              .on("mouseout", mouseout)
              .on("mousemove", (event: MouseEvent, d: TimeSeriesDataPoint) => mousemove(event, [d]))
              .on("click", (event: MouseEvent) => click(event, maneuver));
            circles.exit().remove();
          });
          linePathsSelection.exit().remove();
        }          
      }
    }
  
    function refreshChart(): void {
      if (!idleTimeout) {
        idleTimeout = setTimeout(idled, 150);
        return;
      }

      // Update the scales and redraw the axes
      yScale.domain([minYValue, maxYValue]);
      yAxis.transition().duration(1000).call(d3.axisLeft(yScale).ticks(4));

      xScale.domain([minXValue, maxXValue]);
      xAxis.transition().duration(1000).call(d3.axisBottom(xScale).ticks(10));

      if (collection !== undefined) {
        chartbody.select(".brush").call(brush.move, null);
        chartbody.selectAll(".linePath").remove();
        chartbody.selectAll(".linePath-outline").remove();
        chartbody.selectAll(".hover-circle").remove();

        interface ManeuverPathRefresh {
          eventId: number;
          points: TimeSeriesDataPoint[];
          firstPoint: TimeSeriesDataPoint;
        }
        const maneuversByEvent = new Map<number, TimeSeriesDataPoint[]>();
        collection.forEach(vals => {
          vals.forEach(d => {
            if (!d.event_id) return;
            const eventId = d.event_id;
            if (!maneuversByEvent.has(eventId)) {
              maneuversByEvent.set(eventId, []);
            }
            maneuversByEvent.get(eventId)!.push(d);
          });
        });
        const maneuverPaths: ManeuverPathRefresh[] = [];
        maneuversByEvent.forEach((points, eventId) => {
          points.sort((a: TimeSeriesDataPoint, b: TimeSeriesDataPoint) => a.time - b.time);
          if (points.length > 1 && eventId !== null && eventId !== undefined) {
            maneuverPaths.push({ eventId, points: [...points], firstPoint: points[0] });
          }
        });
    
        const selectedPaths: ManeuverPathRefresh[] = [];
        const nonSelectedPaths: ManeuverPathRefresh[] = [];
        maneuverPaths.forEach(maneuver => {
          if (isEventSelected(maneuver.eventId)) {
            selectedPaths.push(maneuver);
          } else {
            nonSelectedPaths.push(maneuver);
          }
        });
        const orderedPaths = [...nonSelectedPaths, ...selectedPaths];
        const linePathsSelection = chartbody.selectAll(".linePath")
          .data(orderedPaths, (d: ManeuverPathRefresh) => `${d.eventId}-${d.firstPoint.time}`);
        const hasSelection = highlightedEventIds().length > 0;
        
        linePathsSelection
          .enter()
          .append("path")
          .attr("class", (d: ManeuverPathRefresh) => `linePath ${getClass(d.firstPoint)}`)
          .merge(linePathsSelection)
          .each(function(d: ManeuverPathRefresh) {
            const node = this as SVGPathElement;
            const isSelected = hasSelection ? isEventSelected(d.eventId) : false;
            const isColoredBySource = color() === 'SOURCE';
            let strokeColor: string;
            if (!hasSelection) {
              strokeColor = getColor(d.firstPoint);
            } else if (isColoredBySource) {
              strokeColor = isSelected ? getOriginalColor(d.firstPoint) : "lightgrey";
            } else {
              strokeColor = isSelected ? getColor(d.firstPoint) : "lightgrey";
            }
            const strokeWidth = !hasSelection ? 1 : (isSelected ? 3 : 0.5);
            const strokeOpacity = !hasSelection ? 1 : (isSelected ? 1 : 0.5);
            node.style.setProperty("transition", "none", "important");
            node.style.setProperty("stroke", strokeColor, "important");
            node.style.setProperty("stroke-width", String(strokeWidth), "important");
            node.style.setProperty("stroke-opacity", String(strokeOpacity), "important");
          })
          .style("fill", "none")
          .style("pointer-events", "none")
          .attr("transform", "translate(20, 0)")
          .attr("d", (d: ManeuverPathRefresh) => toLinePath(d.points));
        chartbody.selectAll(".linePath-outline").remove();
        chartbody.selectAll(".hover-circle").remove();
        orderedPaths.forEach((maneuver: ManeuverPathRefresh) => {
          const eventKey = String(maneuver.eventId).replace(/[^a-zA-Z0-9_-]/g, "_");
          const circles = chartbody.selectAll(`.hover-circle-${eventKey}`)
            .data(maneuver.points);
          circles.enter()
            .append("circle")
            .attr("class", `hover-circle hover-circle-${eventKey}`)
            .merge(circles)
            .attr("cx", (d: TimeSeriesDataPoint) => (mode_x() ? xScale(d.time) : xScale((d as any).bsp || 0)) + 20)
            .attr("cy", (d: TimeSeriesDataPoint) => yScale((d as any)[channel] || 0))
            .attr("r", 5)
            .style("fill", "transparent")
            .style("stroke", "none")
            .style("pointer-events", "all")
            .style("cursor", "pointer")
            .on("mouseover", (event: MouseEvent, d: TimeSeriesDataPoint) => mouseover(event, [d]))
            .on("mouseout", mouseout)
            .on("mousemove", (event: MouseEvent, d: TimeSeriesDataPoint) => mousemove(event, [d]))
            .on("click", (event: MouseEvent) => click(event, maneuver));
          circles.exit().remove();
        });
        linePathsSelection.exit().remove();
        drawPaths();
      }
    }

    drawPaths()
  }

  function updateSelection() {
    updateMinMaxRanges()

    // Cache expensive computations
    const selected = highlightedEventIds();
    const selectedSet = new Set(selected);
    const hasSelection = selected.length > 0;
    const darkMode = isDark();

    // Batch DOM updates using requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      const root = containerRef;
      const svgs = root ? Array.from(root.querySelectorAll("svg.ts")) : [];

      if (hasSelection) {
        for (const svg of svgs) {
          const chartBody = d3.select(svg).select(":scope > g");
          if (chartBody.empty()) continue;
          
          // Get all lines at once instead of filtering twice
          const allLines = chartBody.selectAll(".linePath");
          
          // Batch style updates using D3's selection methods
          allLines.each(function (d: any) {
            const node = this as SVGPathElement;
            const maneuver = d as { eventId?: number; firstPoint?: TimeSeriesDataPoint; points?: TimeSeriesDataPoint[] };
            const eventId = maneuver?.eventId ?? (maneuver?.firstPoint?.event_id ?? null);
            
            if (!node || eventId === null) return;
            
            const isSelected = selectedSet.has(eventId);
            const firstPoint = maneuver.firstPoint || (maneuver.points && maneuver.points[0]) || null;
            
            if (!firstPoint) return;
            
            if (isSelected) {
              node.style.setProperty("transition", "none", "important");
              node.style.setProperty("stroke", getColor(firstPoint), "important");
              node.style.setProperty("stroke-width", "3", "important");
              node.style.setProperty("stroke-opacity", "1", "important");
            } else {
              node.style.setProperty("stroke", "lightgrey", "important");
              node.style.setProperty("transition", "none", "important");
              node.style.setProperty("stroke-width", "0.5", "important");
              node.style.setProperty("stroke-opacity", "0.2", "important");
            }
            
            // Remove outline if it exists (only check once)
            const outline = (node.parentNode as Element | null)?.querySelector(`.linePath-outline[data-event-id="${eventId}"]`);
            if (outline) {
              outline.remove();
            }
          });
        }
      } else {
        // No selection - restore original colors
        for (const svg of svgs) {
          const chartBody = d3.select(svg).select(":scope > g");
          if (chartBody.empty()) continue;
          
          const allLines = chartBody.selectAll(".linePath");
          
          allLines.each(function (d: any) {
            const node = this as SVGPathElement;
            const maneuver = d as { eventId?: number; firstPoint?: TimeSeriesDataPoint; points?: TimeSeriesDataPoint[] };
            const eventId = maneuver?.eventId ?? (maneuver?.firstPoint?.event_id ?? null);
            const firstPoint = maneuver?.firstPoint ?? (maneuver?.points && maneuver.points[0]) ?? null;
            
            if (!node) return;
            
            if (firstPoint) {
              node.style.setProperty("transition", "none", "important");
              node.style.setProperty("stroke", getColor(firstPoint), "important");
              node.style.setProperty("stroke-width", "1", "important");
              node.style.setProperty("stroke-opacity", "1", "important");
            }
            
            if (eventId != null) {
              const outline = (node.parentNode as Element | null)?.querySelector(`.linePath-outline[data-event-id="${eventId}"]`);
              if (outline) outline.remove();
            }
          });
        }
      }

      setTriggerSelection(false);

      // Banner/hasSelection reflects MANUAL selection only; the live highlight layer must not set it.
      if (selectedEvents().length > 0) {
        setHasSelection(true);
      } else {
        // Don't clear hasSelection when the other panel has a brush selection (e.g. map timeseries brushed in split view)
        const hasBrushRange = selectedRange() && selectedRange().length > 0;
        if (!hasBrushRange) {
          setHasSelection(false);
          setSelection([]);
        }
      }
    });
  }

  // Event types to target for a tag write - a single selected type, else all maneuver types.
  const maneuverEventTypes = (): string[] => {
    const currentEventType = eventType();
    return currentEventType ? [currentEventType] : MANEUVER_EVENT_TYPES;
  };

  // Shared post-write handling for GRADE and STATE updates.
  const afterTagUpdate = (): void => {
    // Trigger parent component to refetch data
    if (props.onDataUpdate && typeof props.onDataUpdate === 'function') {
      props.onDataUpdate();
    } else {
      // Fallback: trigger update
      setTriggerUpdate(true);
    }
  };

  // Function to perform the GRADE update
  const performGradeUpdate = async (gradeValue: number, selected: number[]): Promise<void> => {
    // Get required values from persistent store
    const { selectedClassName, selectedProjectId } = persistantStore;
    const className = selectedClassName();
    const projectId = selectedProjectId();

    if (!className || !projectId) {
      logError('Cannot update GRADE: missing class_name or project_id');
      return;
    }

    const eventTypes = maneuverEventTypes();

    try {
      // Call admin API to update GRADE for selected events
      const response = await putData(`${apiEndpoints.admin.events}/tags`, {
        class_name: className,
        project_id: projectId,
        events: selected,
        event_types: eventTypes,
        key: 'GRADE',
        value: gradeValue
      });
      
      if (response.success) {
        debug(`Successfully updated GRADE to ${gradeValue} for ${selected.length} event(s)`);
        
        // Grade update in HuniDB will be implemented when available
        // Currently grade updates are handled on next data fetch
        
        // Update grade filter to include the newly graded value
        const currentGrades = selectedGradesManeuvers();
        const gradeValueStr = String(gradeValue);
        if (!currentGrades.includes(gradeValueStr)) {
          setSelectedGradesManeuvers([...currentGrades, gradeValueStr]);
          debug(`Added grade ${gradeValue} to selectedGrades filter`);
        }

        afterTagUpdate();
      } else {
        logError('Failed to update GRADE:', response.message || 'Unknown error');
      }
    } catch (error: any) {
      logError('Error updating GRADE:', error);
    }
  };

  /**
   * Rebuild every chart that already has data: each loaded multi-group section, or the single
   * series. Used by the resize handler (width change) and by the selectedOnly selection effect,
   * which must rebuild rather than recolor so X/Y rescale to the new selection.
   */
  function rebuildLoadedCharts() {
    if (isMultiGroupMode()) {
      const map = groupSeriesState();
      for (const k of Object.keys(map)) {
        const ent = map[k];
        if (ent.status !== "loaded" || !ent.data?.length) { continue; }
        const m = groupPlotMounts.get(k);
        if (m) {
          buildTimeSeries(ent.data, {
            mountEl: m,
            groupKey: k,
            chartsOrder: ent.charts ?? [],
            channelOrderStored: ent.channelOrder ?? [],
          });
        }
      }
    } else if (tsdata().length > 0) {
      buildTimeSeries(tsdata());
    }
  }

  /**
   * The averaged baseline traces are fetched, so they arrive after the charts have drawn. Rebuild
   * then, so the reference appears without waiting for an unrelated redraw.
   *
   * Deferred: on mount the charts are built by their own path, and rebuilding on top of that would
   * throw away the work that just finished.
   */
  createEffect(
    on(
      () => [
        maneuverBaselineStore.baselineActive(),
        maneuverBaselineStore.baselineTracks(),
        maneuverBaselineStore.baselineLoading(),
      ],
      () => {
        if (maneuverBaselineStore.baselineLoading()) { return; }
        untrack(() => rebuildLoadedCharts());
      },
      { defer: true }
    )
  );

  // Function to calculate and set container height
  const updateContainerHeight = () => {
    if (!containerRef) return;

    // Side panel: fill the slot and scroll internally, leaving the toggle button below visible.
    // Sized from the DOM rather than the window, so it never touches #timeseries-area or the
    // --scale-factor math.
    if (props.fitParent) {
      const parentEl = containerRef.parentElement;
      // MAP view: prefer the sibling map's own height. .maneuver-map carries min-height: 750px and
      // therefore overflows its container, so the flex row (and our parent slot) is SHORTER than the
      // map you actually see — measuring the parent leaves the chart visibly short. Resolved through
      // our own layout root rather than a global #map-area lookup so split view stays correct.
      const layoutRoot = containerRef.closest(".maneuver-map-layout") as HTMLElement | null;
      const mapEl = layoutRoot?.querySelector(".maneuver-map") as HTMLElement | null;
      const mapPx = mapEl?.clientHeight ?? 0;
      const avail = mapPx > 0
        ? mapPx + MAP_SIDE_PANEL_EXTRA_HEIGHT_PX
        : (parentEl ? parentEl.clientHeight : 0);
      const layoutPx = Math.max(200, avail || 400);
      if (lastAppliedLayoutHeightPx > 0 && Math.abs(layoutPx - lastAppliedLayoutHeightPx) < 3) {
        return;
      }
      lastAppliedLayoutHeightPx = layoutPx;
      suppressSelfResizeOnce = true;
      containerRef.style.setProperty("height", `${layoutPx}px`, "important");
      containerRef.style.setProperty("max-height", `${layoutPx}px`, "important");
      containerRef.style.setProperty("overflow-y", isMacOS() ? "scroll" : "auto", "important");
      return;
    }

    // Solo ManeuverWindow: scroll viewport height is owned by setupMediaContainerScaling
    // (applyManeuverWindowScrollViewport in global.ts) — see docs/frontend/maneuver-window-scaling.md.
    if (props.instanceId) {
      containerRef.style.removeProperty("min-height");
      containerRef.style.removeProperty("height");
      containerRef.style.removeProperty("max-height");
      const tsArea = document.querySelector(`[data-maneuver-ts-area="${props.instanceId}"]`);
      if (tsArea instanceof HTMLElement) {
        tsArea.style.removeProperty("min-height");
        tsArea.style.removeProperty("height");
      }
      lastAppliedLayoutHeightPx = 0;
      return;
    }

    // Find the media-container parent that has the scale transform
    let parent = containerRef.parentElement;
    let mediaContainer: HTMLElement | null = null;
    while (parent) {
      if (parent.id === 'media-container') {
        mediaContainer = parent;
        break;
      }
      parent = parent.parentElement;
    }
    
    // Get the scale factor from CSS custom property or calculate from transform
    let scaleFactor = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--scale-factor')) || 1;
    
    // If we found the media-container, try to get scale from its computed transform
    if (mediaContainer) {
      const transform = getComputedStyle(mediaContainer).transform;
      if (transform && transform !== 'none') {
        // Parse matrix scale: matrix(scaleX, skewY, skewX, scaleY, translateX, translateY)
        const matrix = transform.match(/matrix\(([^)]+)\)/);
        if (matrix) {
          const values = matrix[1].split(',').map(v => parseFloat(v.trim()));
          if (values.length >= 4) {
            // scaleX is the first value in the matrix
            scaleFactor = values[0];
          }
        }
      }
    }
    
    // The shared color legend row sits above this view on every host; it is the only legend
    // now, and it is measured (never a constant) because it rewraps with the color mode.
    const legendHeight = maneuverColorLegendRenderedPx();
    
    // In split view use the same height logic as fullscreen: fill the available space (match setupMediaContainerScaling formula)
    const splitPanel = mediaContainer?.closest('.split-panel') as HTMLElement | null;
    const headerHeight = 60; // Account for header (only in fullscreen); matches maneuvers-page reserve in global.ts
    const padding = 20; // Some padding for spacing
    const inManeuverWindow = Boolean(props.instanceId);
    let availableViewportHeight: number;
    if (splitPanel) {
      // Use panel height minus 60px reserve (same as setupMediaContainerScaling for maneuvers-page) so we fill the panel regardless of timing
      availableViewportHeight = Math.max(200, splitPanel.clientHeight - 60);
    } else if (inManeuverWindow) {
      // Solo maneuver window: Window hides header; #main-content is the scroll/flex viewport
      const mainContent = document.getElementById("main-content");
      availableViewportHeight = Math.max(
        200,
        mainContent?.clientHeight ?? window.innerHeight
      );
    } else {
      availableViewportHeight = window.innerHeight;
    }
    // ManeuverWindow uses hideHeader — do not subtract a phantom 60px header band
    const effectiveHeaderHeight = splitPanel || inManeuverWindow ? 0 : headerHeight;
    const desiredVisibleHeight = availableViewportHeight - effectiveHeaderHeight - legendHeight - padding;
    
    // When content is scaled down, we need MORE layout height to achieve the desired visible height
    // Formula: layoutHeight = desiredVisibleHeight / scaleFactor
    const layoutHeight = desiredVisibleHeight / scaleFactor;
    
    // Debug logging
    debug('Height calculation:', {
      windowInnerHeight: window.innerHeight,
      scaleFactorFromCSS: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--scale-factor')) || 1,
      scaleFactorFromTransform: scaleFactor,
      headerHeight,
      legendHeight,
      padding,
      desiredVisibleHeight,
      layoutHeight,
      finalHeight: Math.max(400, layoutHeight),
      containerStyleHeight: containerRef.style.height,
      containerComputedHeight: getComputedStyle(containerRef).height
    });
    
    const layoutPx = Math.max(400, layoutHeight);
    const isFullPageMode = !splitPanel && !props.instanceId;
    const chartStackHeightPx = isFullPageMode ? getRenderedChartStackHeight(scaleFactor) : 0;
    const appliedHeightPx = isFullPageMode ? Math.max(layoutPx, chartStackHeightPx) : layoutPx;
    if (lastAppliedLayoutHeightPx > 0 && Math.abs(appliedHeightPx - lastAppliedLayoutHeightPx) < 3) {
      return;
    }
    lastAppliedLayoutHeightPx = appliedHeightPx;

    // About to write container/area heights: suppress the single ResizeObserver cycle our write triggers
    // so re-measuring the (now-taller) plotsRoot can't feed back and grow the container again.
    suppressSelfResizeOnce = true;

    // Set height to fill available space (use !important to override CSS)
    const heightValue = `${appliedHeightPx}px`;
    containerRef.style.setProperty('min-height', heightValue, 'important');
    containerRef.style.setProperty('height', heightValue, 'important');
    if (isFullPageMode && appliedHeightPx > layoutPx + 3) {
      containerRef.style.removeProperty('max-height');
    } else {
      containerRef.style.setProperty('max-height', `${layoutPx}px`, 'important');
    }
    
    // Parent timeseries region: prefer data attribute when instance-scoped
    const timeseriesArea = props.instanceId
      ? document.querySelector(`[data-maneuver-ts-area="${props.instanceId}"]`)
      : document.getElementById('timeseries-area');
    if (timeseriesArea) {
      // Set min-height to ensure it can accommodate the child
      timeseriesArea.style.setProperty('min-height', heightValue, 'important');
      timeseriesArea.style.setProperty('height', 'auto', 'important');
      if (isFullPageMode) {
        timeseriesArea.style.setProperty('max-height', 'none', 'important');
        timeseriesArea.style.setProperty('overflow-y', 'visible', 'important');
      } else {
        timeseriesArea.style.removeProperty('max-height');
        timeseriesArea.style.removeProperty('overflow-y');
      }
    }
    
    // Verify what was actually rendered (this will be the scaled height)
    setTimeout(() => {
      const actualHeight = containerRef?.getBoundingClientRect().height;
      const computedHeight = containerRef ? getComputedStyle(containerRef).height : 'N/A';
      const timeseriesAreaHeight = timeseriesArea ? timeseriesArea.getBoundingClientRect().height : 'N/A';
      debug('Actual rendered height (scaled):', actualHeight);
      debug('Computed style height:', computedHeight);
      debug('Timeseries area height (scaled):', timeseriesAreaHeight);
    }, 100);
  };

  onMount(() => {
    const cleanupFns: (() => void)[] = [];
    // Initial load: triggerUpdate + filtered() effects call updateTimeSeries (avoid triple fetch with onMount)

    // Calculate and set container height
    setTimeout(() => {
      updateContainerHeight();
    }, 100);

    const RESIZE_DEBOUNCE_MS = 120;
    const WIDTH_EPS = 2;

    const runDebouncedResize = () => {
      if (resizeDebounceTimer) {
        clearTimeout(resizeDebounceTimer);
        resizeDebounceTimer = null;
      }
      resizeDebounceTimer = setTimeout(() => {
        resizeDebounceTimer = null;
        const w =
          multiPlotsRootEl?.clientWidth ?? plotsEl?.clientWidth ?? containerRef?.clientWidth ?? 0;
        const widthChanged = Math.abs(w - lastResizeObservedWidth) > WIDTH_EPS;
        if (w > 0) {
          lastResizeObservedWidth = w;
        }
        // Skip the cycle our own height write triggered (no width change) to break the measure->grow loop.
        // Genuine width changes and external (split-drag) height changes still fall through and reflow.
        if (suppressSelfResizeOnce && !widthChanged) {
          suppressSelfResizeOnce = false;
          return;
        }
        suppressSelfResizeOnce = false;
        // Rebuild when width meaningfully changes. Previously we skipped ResizeObserver for instanceId
        // (maneuver popup) to avoid scrollbar/flex oscillation — that blocked reflow when only the
        // container grew (split drag or popup resize without a reliable window resize event).
        if (widthChanged) {
          updateChartWidth();
          rebuildLoadedCharts();
        }
        updateContainerHeight();
      }, RESIZE_DEBOUNCE_MS);
    };

    // Set up resize observer for responsive charts (debounced; rebuild only on meaningful width change)
    const resizeObserver = new ResizeObserver(() => {
      runDebouncedResize();
    });

    // Window resize handler as fallback
    const handleWindowResize = () => {
      runDebouncedResize();
    };

    // Use setTimeout to ensure containerRef is available
    setTimeout(() => {
      if (containerRef) {
        resizeObserver.observe(containerRef);
      }
      // In split view the panel may not have final size yet; observe the split panel so height updates when panel is sized or resized
      const panel = containerRef?.closest('.split-panel') as HTMLElement | null;
      if (panel) {
        resizeObserver.observe(panel);
        // Delayed recalc so we run after layout and setupMediaContainerScaling; 1000ms catches late layout in split view
        const t1 = setTimeout(updateContainerHeight, 300);
        const t2 = setTimeout(updateContainerHeight, 600);
        const t3 = setTimeout(updateContainerHeight, 1000);
        cleanupFns.push(() => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); });
        cleanupFns.push(() => { if (panel) resizeObserver.unobserve(panel); });
      }
      // Side panel: the slot is sized after the video/map renders; observe it and recalc on a few
      // delays so the chart fills the column and scrolls internally once laid out.
      if (props.fitParent && containerRef?.parentElement) {
        const p = containerRef.parentElement;
        resizeObserver.observe(p);
        // MAP view: the map drives our height, so track the map element itself — the parent slot
        // stops changing once the row settles even though the map keeps resizing (zoom, split drag).
        const layoutRoot = containerRef.closest(".maneuver-map-layout") as HTMLElement | null;
        const mapEl = layoutRoot?.querySelector(".maneuver-map") as HTMLElement | null;
        if (mapEl) {
          resizeObserver.observe(mapEl);
          cleanupFns.push(() => { resizeObserver.unobserve(mapEl); });
        }
        const f1 = setTimeout(updateContainerHeight, 150);
        const f2 = setTimeout(updateContainerHeight, 400);
        const f3 = setTimeout(updateContainerHeight, 800);
        cleanupFns.push(() => { clearTimeout(f1); clearTimeout(f2); clearTimeout(f3); });
        cleanupFns.push(() => { resizeObserver.unobserve(p); });
      }
    }, 100);

    // Add window resize listener
    window.addEventListener('resize', handleWindowResize);
    
    // Add keyboard listener for GRADE updates (0-5 keys)
    const handleKeyPress = (event: KeyboardEvent) => {
      try {
        // Don't trigger if user is typing in an input field or textarea
        const target = event.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
          return;
        }
        
        // Only handle if there are selected events
        const selected = selectedEvents();
        if (!selected || selected.length === 0) {
          return;
        }
        
        const key = event.key;
        if (shouldBlockGradeLikeHotkeysForPerformanceSelections(key)) {
          return;
        }

        if (!['0', '1', '2', '3', '4', '5'].includes(key)) {
          return;
        }

        // Check if user is NOT a reader (readers cannot grade)
        const currentUser = user();
        if (!currentUser) {
          return;
        }
        
        // Superusers can always grade
        if (currentUser.is_super_user) {
          // Allow grading
        } else {
          // Check if user is a reader - readers cannot grade
          const userPermissions = currentUser.permissions;
          let isReader = false;
          
          if (typeof userPermissions === 'string') {
            isReader = userPermissions === 'reader';
          } else if (typeof userPermissions === 'object' && userPermissions !== null) {
            const permissionValues = Object.values(userPermissions);
            // User is a reader if ALL their permissions are 'reader'
            isReader = permissionValues.length > 0 && permissionValues.every(p => p === 'reader');
          }
          
          if (isReader) {
            return; // Readers cannot grade
          }
        }
        
        // Prevent default behavior and stop propagation
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Show confirmation dialog before proceeding
        const message = `Are you sure you want to update GRADE to ${parseInt(key, 10)} for ${selected.length} selected event(s)?\n\nThis action will modify the data and cannot be easily undone.`;

        // Use window.confirm - it automatically closes when user clicks OK or Cancel
        const confirmed = window.confirm(message);
        if (!confirmed) {
          // User cancelled - dialog is already closed
          return;
        }

        // User confirmed - proceed with the update
        // Call asynchronously to avoid blocking the keydown handler
        setTimeout(() => {
          const update = performGradeUpdate(parseInt(key, 10), selected);
          update.catch(error => {
            logError('Error in maneuver hotkey update:', error);
          });
        }, 0);
      } catch (error: any) {
        logError('Error in handleKeyPress:', error);
        event.preventDefault();
        event.stopPropagation();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);

    onCleanup(() => {
      // Clean up D3 selections and SVG elements
      try {
        if (plotsEl) {
          d3.select(plotsEl).selectAll("*").remove();
        }
        if (multiPlotsRootEl) {
          d3.select(multiPlotsRootEl).selectAll("*").remove();
        }
        groupPlotMounts.forEach((el) => {
          if (el) d3.select(el).selectAll("*").remove();
        });
        groupPlotMounts.clear();
        groupSectionObservers.forEach((o) => o.disconnect());
        groupSectionObservers.clear();
      } catch (error) {
        logError('TimeSeries: Error cleaning up D3 selections:', error);
      }
      
      // Clear arrays to free memory
      timeArray = [];
      valArray = [];
      eidArray = [];

      cleanupFns.forEach((fn) => fn());
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('keydown', handleKeyPress);

      if (resizeDebounceTimer) {
        clearTimeout(resizeDebounceTimer);
        resizeDebounceTimer = null;
      }

      // Clear any pending selection updates
      if (selectionUpdateTimeout) {
        clearTimeout(selectionUpdateTimeout);
        selectionUpdateTimeout = null;
      }
      
      // Clean up IntersectionObservers
      intersectionObservers.forEach((observer) => {
        observer.disconnect();
      });
      intersectionObservers.clear();
      storedMouseHandlers.clear();
      
      // Clear data to free memory
      setTsData([]);
      setChartVisibility(new Map());
      
      debug('TimeSeries: Cleanup complete - all resources cleared');
    });
  });

  // Shared with trigger + filtered effects: backup must not schedule after trigger path cleared the flag (same commit).
  let lastFilteredCount = 0;
  let lastFilteredHash = '';
  let filteredUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

  const getFilteredHash = (arr: unknown[]): string => {
    if (!Array.isArray(arr) || arr.length === 0) return '';
    if (typeof arr[0] === 'number') {
      const sorted = [...(arr as number[])].sort((a, b) => a - b);
      return sorted.slice(0, 10).join(',') + `_${arr.length}`;
    }
    const ids = (arr as { event_id?: number }[])
      .map((o) => o?.event_id)
      .filter((id): id is number => typeof id === 'number');
    const sorted = [...ids].sort((a, b) => a - b);
    return sorted.slice(0, 10).join(',') + `_${arr.length}`;
  };

  const extractFilteredEventIds = (): number[] => {
    const currentFiltered = filtered();
    if (!Array.isArray(currentFiltered) || currentFiltered.length === 0) return [];
    if (typeof currentFiltered[0] === "number") {
      return currentFiltered as unknown as number[];
    }
    return (currentFiltered as { event_id?: number }[])
      .map((item) => item.event_id)
      .filter((id): id is number => typeof id === "number");
  };

  createEffect(() => {
    if (!isMultiGroupMode()) return;
    const keys = groupOptionKeys();
    setGroupSeriesState((prev) => {
      const next = { ...prev };
      let changed = false;
      keys.forEach((k) => {
        if (!next[k]) {
          next[k] = { status: "idle" };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  });

  const startGroupFetchForOption = (groupKey: string) => {
    if (!isMultiGroupMode() || groupKey === "BASICS") return;
    const eventIds = extractFilteredEventIds();
    if (eventIds.length === 0) return;
    const prev = groupSeriesState()[groupKey];
    if (prev?.status === "loading" || prev?.status === "loaded") return;

    const session = multiSessionCounter;
    const signal = multiFetchAbort?.signal;
    setGroupSeriesState((p) => ({ ...p, [groupKey]: { status: "loading" } }));
    const desc = buildTimeseriesDescription(phase(), groupKey);
    fetchTimeSeriesData(context, {
      eventType: eventType(),
      description: desc,
      eventList: eventIds,
      signal,
    })
      .then((resp) => {
        if (session !== multiSessionCounter) return;
        const parsed = parseTimeSeriesResponse({
          timeseriesResponse: resp,
          eventIds,
          isEventHidden,
          tableRows: tabledata() as Array<Record<string, unknown>>,
          phaseVal: phase(),
        });
        const dataArr = parsed.dataArray as TimeSeriesDataPoint[][];
        setGroupSeriesState((p) => ({
          ...p,
          [groupKey]: {
            status: "loaded",
            data: dataArr,
            charts: parsed.charts,
            channelOrder: parsed.channelOrder,
          },
        }));
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (session !== multiSessionCounter) return;
            const mount = groupPlotMounts.get(groupKey);
            if (mount && dataArr.length > 0) {
              buildTimeSeries(dataArr, {
                mountEl: mount,
                groupKey,
                chartsOrder: parsed.charts,
                channelOrderStored: parsed.channelOrder,
              });
            }
            updateSelection();
          });
        });
      })
      .catch((err: unknown) => {
        if (session !== multiSessionCounter) return;
        logError("TimeSeries: multi group fetch failed", err);
        setGroupSeriesState((p) => ({ ...p, [groupKey]: { status: "error" } }));
      });
  };

  const flushMultiGroupUpdate = () => {
    if (firstload()) {
      setLoading(true);
    }
    multiSessionCounter += 1;
    const session = multiSessionCounter;
    if (multiFetchAbort) {
      multiFetchAbort.abort();
    }
    multiFetchAbort = new AbortController();
    const signal = multiFetchAbort.signal;

    groupSectionObservers.forEach((o) => o.disconnect());
    groupSectionObservers.clear();
    groupPlotMounts.forEach((el) => {
      if (el) d3.select(el).selectAll("*").remove();
    });
    intersectionObservers.forEach((o) => o.disconnect());
    intersectionObservers.clear();
    setChartVisibility(new Map());

    const keys = groupOptionKeys();
    const blank: Record<string, GroupSeriesEntry> = {};
    keys.forEach((k) => {
      blank[k] = { status: "idle" };
    });
    setGroupSeriesState(blank);
    setTsData([]);
    timeArray = [];
    valArray = [];
    eidArray = [];

    const eventIds = extractFilteredEventIds();
    if (eventIds.length === 0) {
      setLoading(false);
      setFirstLoad(false);
      return;
    }

    setGroupSeriesState((prev) => ({ ...prev, BASICS: { status: "loading" } }));
    const desc = buildTimeseriesDescription(phase(), "BASICS");
    fetchTimeSeriesData(context, {
      eventType: eventType(),
      description: desc,
      eventList: eventIds,
      signal,
    })
      .then((resp) => {
        if (session !== multiSessionCounter) return;
        const parsed = parseTimeSeriesResponse({
          timeseriesResponse: resp,
          eventIds,
          isEventHidden,
          tableRows: tabledata() as Array<Record<string, unknown>>,
          phaseVal: phase(),
        });
        const dataArr = parsed.dataArray as TimeSeriesDataPoint[][];
        setGroupSeriesState((prev) => ({
          ...prev,
          BASICS: {
            status: "loaded",
            data: dataArr,
            charts: parsed.charts,
            channelOrder: parsed.channelOrder,
          },
        }));
        setTsData(dataArr);
        updateMinMaxRanges();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (session !== multiSessionCounter) return;
            const mount = groupPlotMounts.get("BASICS");
            if (mount && dataArr.length > 0) {
              buildTimeSeries(dataArr, {
                mountEl: mount,
                groupKey: "BASICS",
                chartsOrder: parsed.charts,
                channelOrderStored: parsed.channelOrder,
              });
            }
          });
        });
      })
      .catch((err: unknown) => {
        if (session !== multiSessionCounter) return;
        logError("TimeSeries: multi BASICS fetch failed", err);
        setGroupSeriesState((prev) => ({ ...prev, BASICS: { status: "error" } }));
      })
      .finally(() => {
        if (session !== multiSessionCounter) return;
        setLoading(false);
        setFirstLoad(false);
        const clearAfterLoad = () => {
          updateSelection();
        };
        if (props.instanceId) {
          queueMicrotask(clearAfterLoad);
        } else {
          setTimeout(clearAfterLoad, 500);
        }
        requestAnimationFrame(() => {
          groupOptionKeys()
            .filter((gk) => gk !== "BASICS")
            .forEach((gk) => {
              const el = containerRef?.querySelector(`[data-ts-group-section="${gk}"]`) as HTMLElement | null;
              if (!el) return;
              if (groupSectionObservers.has(gk)) return;
              const obs = new IntersectionObserver(
                (entries) => {
                  entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    untrack(() => startGroupFetchForOption(gk));
                  });
                },
                { rootMargin: "300px 0px 300px 0px", threshold: 0.01 }
              );
              obs.observe(el);
              groupSectionObservers.set(gk, obs);
            });
        });
      });
  };

  const flushUpdateTimeSeries = () => {
    if (isMultiGroupMode()) {
      flushMultiGroupUpdate();
      return;
    }
    if (firstload()) {
      setLoading(true);
    }
    fetchData()
      .then((data) => {
        // buildTimeSeries will use charts from timeseries data if available
        buildTimeSeries(data);
        setFirstLoad(false);
      })
      .catch((err: unknown) => {
        logError("TimeSeries: Failed to refresh time series data", err);
      })
      .finally(() => {
        // Always clear loading (buildTimeSeries throw or fetch failure left it stuck at 0.2 opacity before).
        // Always sync line styles to current selection after refresh — clearing selection must restore full opacity like initial load.
        const clearAfterLoad = () => {
          setLoading(false);
          if (tsdata().length > 0) {
            updateSelection();
          }
        };
        // ManeuverWindow: no 500ms fade hold — charts should go to full opacity as soon as data is built.
        if (props.instanceId) {
          queueMicrotask(clearAfterLoad);
        } else {
          setTimeout(clearAfterLoad, 500);
        }
      });
  };

  /** Debounce: multiple effects (trigger, filtered backup, description) must not each start a full fetch. */
  const updateTimeSeries = () => {
    if (updateTimeSeriesDebounce) {
      clearTimeout(updateTimeSeriesDebounce);
    }
    const ms = firstload() ? 0 : 48;
    updateTimeSeriesDebounce = setTimeout(() => {
      updateTimeSeriesDebounce = null;
      flushUpdateTimeSeries();
    }, ms);
  };

  createEffect(() => {
    if (!isPanelActive()) return;
    if (triggerUpdate()) {
      setTriggerUpdate(false);
      if (filteredUpdateTimeout) {
        clearTimeout(filteredUpdateTimeout);
        filteredUpdateTimeout = null;
      }
      const arr = filtered();
      const n = Array.isArray(arr) ? arr.length : 0;
      if (n > 0) {
        lastFilteredHash = getFilteredHash(arr);
        lastFilteredCount = n;
      }
      queueMicrotask(() => {
        updateTimeSeries();
      });
    }
  });

  // Watch for filtered data changes - update timeseries when data becomes available
  // This serves as a backup in case triggerUpdate doesn't fire or gets cleared
  // Use debouncing to handle rapid re-renders during initial load
  createEffect(() => {
    if (!isPanelActive()) return;
    const currentFiltered = filtered();
    const currentFilteredCount = currentFiltered?.length || 0;
    const hasTriggerUpdate = untrack(() => triggerUpdate());

    const filteredHash = getFilteredHash(Array.isArray(currentFiltered) ? currentFiltered : []);
    
    // Clear any pending timeout
    if (filteredUpdateTimeout) {
      clearTimeout(filteredUpdateTimeout);
      filteredUpdateTimeout = null;
    }
    
    // Reset when filtered data is cleared
    if (currentFilteredCount === 0) {
      lastFilteredCount = 0;
      lastFilteredHash = '';
      return;
    }
    
    // Transition from empty to non-empty: always schedule load (handles cross-window sync / initial load race)
    const justGotData = lastFilteredCount === 0 && currentFilteredCount > 0;
    const dataChanged = filteredHash !== lastFilteredHash || justGotData;
    
    if (currentFilteredCount > 0 && dataChanged && !hasTriggerUpdate) {
      const delay = justGotData ? 50 : 100; // Shorter delay when we first get data so timeseries loads promptly
      filteredUpdateTimeout = setTimeout(() => {
        const stillHasTrigger = untrack(() => triggerUpdate());
        const currentHash = getFilteredHash(Array.isArray(filtered()) ? filtered() : []);
        const shouldUpdate =
          !stillHasTrigger &&
          (currentHash !== lastFilteredHash || justGotData) &&
          currentHash.length > 0;
        if (shouldUpdate) {
          updateTimeSeries();
          lastFilteredHash = currentHash;
        }
        filteredUpdateTimeout = null;
      }, delay);
    }
    
    lastFilteredCount = currentFilteredCount;
    if (filteredHash && !filteredUpdateTimeout) {
      lastFilteredHash = filteredHash;
    }
  });
  
  // Cleanup timeout on unmount
  onCleanup(() => {
    if (filteredUpdateTimeout) {
      clearTimeout(filteredUpdateTimeout);
      filteredUpdateTimeout = null;
    }
    if (updateTimeSeriesDebounce) {
      clearTimeout(updateTimeSeriesDebounce);
      updateTimeSeriesDebounce = null;
    }
    // The playhead is owned by this panel; leaving it set would strand a boat on the map.
    if (props.playhead) { clearManeuverPlayhead(); }
  });

  // Legacy single-group: description or phase changes refetch
  createEffect(on([() => props?.description, phase], () => {
    if (isMultiGroupMode()) return;
    if (untrack(() => !firstload() && tsdata().length > 0)) {
      updateTimeSeries();
    }
  }));

  // Multi-group: phase changes reset sections and refetch BASICS (not driven by description prop)
  createEffect(
    on(phase, () => {
      if (!isMultiGroupMode()) return;
      if (untrack(() => firstload())) return;
      updateTimeSeries();
    })
  );

  createEffect(() => {
    if (!isMultiGroupMode()) return;
    const cn = persistantStore.selectedClassName();
    const pid = persistantStore.selectedProjectId();
    if (!cn || !pid) {
      setGroupOptionKeys(["BASICS"]);
      return;
    }
    let active = true;
    void (async () => {
      const opts = await fetchManeuverTimeseriesOptions(String(cn), String(pid));
      if (!active) return;
      setGroupOptionKeys(opts.length ? opts : ["BASICS"]);
    })();
    onCleanup(() => {
      active = false;
    });
  });

  createEffect(() => {
    if (triggerSelection()) {
      updateSelection()
    }
  });

  // Watch for selectedEvents changes (from cross-window sync) and update selection
  // Use debouncing to prevent rapid updates during legend selection
  let selectionUpdateTimeout: NodeJS.Timeout | null = null;
  createEffect(() => {
    // Access selection + live highlight to trigger effect when either changes
    highlightedEventIds();

    // A playhead position is maneuver-relative, so it's meaningless against a different selection —
    // drop it so the map stops drawing a boat for maneuvers that are no longer shown.
    if (props.playhead) { clearManeuverPlayhead(); }

    // Update selection when selectedEvents changes (handles cross-window sync and local clicks)
    // Only update if component is ready (data loaded)
    if (tsdata().length > 0) {
      // Clear any pending update
      if (selectionUpdateTimeout) {
        clearTimeout(selectionUpdateTimeout);
      }
      
      // Debounce updates to batch rapid changes (e.g., when clicking multiple legend items)
      selectionUpdateTimeout = setTimeout(() => {
        // selectedOnly renders only the selected maneuvers, so a selection change must rebuild
        // (to rescale X/Y to the new set) rather than just recolor the existing paths.
        if (props.selectedOnly) {
          untrack(() => rebuildLoadedCharts());
        } else {
          updateSelection();
        }
        selectionUpdateTimeout = null;
      }, 50); // 50ms debounce - fast enough to feel responsive, slow enough to batch updates
    }
  });

  // VIDEO playhead animation: reposition every chart's red line as playback advances.
  // maneuverVideoElapsedSeconds() is the maneuver-relative elapsed seconds (0 at the maneuver
  // moment, -15 at clip start) shared by the video tiles and the ManeuverPlayPause counter.
  // Read only here — never inside the chart-rebuild effects, or every 100ms tick would rebuild.
  createEffect(() => {
    if (!props.playhead) { return; }
    const sec = maneuverVideoElapsedSeconds();
    playheadUpdaters.forEach((update) => update(sec));
  });

  // Watch for color changes - update selection when color changes
  // This ensures the maneuver window updates when color changes in the main window
  createEffect(() => {
    const currentColor = color();
    
    // Update selection when color changes and component is ready (data loaded)
    if (tsdata().length > 0) {
      // Use untrack to prevent updateSelection from creating reactive dependencies
      untrack(() => {
        updateSelection();
      });
    }
  });


  return (
    <>
      {firstload() && <Loading class="maneuver-ts-loading-overlay" />}
      <div
        class="time-series"
        ref={(el) => {
          containerRef = el;
        }}
        style={{
          opacity: loading() ? 0.2 : 1,
          "pointer-events": loading() ? "none" : "auto",
        }}
      >
        <Show
          when={isMultiGroupMode()}
          fallback={
            <div
              class="maneuver-time-series maneuver-ts-plots-root"
              ref={(el) => {
                plotsEl = el;
              }}
            />
          }
        >
          <div
            class="maneuver-time-series maneuver-ts-multi-root"
            ref={(el) => {
              multiPlotsRootEl = el;
              plotsEl = null;
            }}
          >
            <For each={groupOptionKeys()}>
              {(gk) => (
                  <div class="group-container" data-ts-group-section={gk}>
                    <div class="break">
                      <h2>{gk}</h2>
                    </div>
                    <Show when={gk !== "BASICS" && groupSeriesState()[gk]?.status !== "loaded"}>
                      <div class="maneuver-ts-group-placeholder" aria-hidden="true" />
                    </Show>
                    <div
                      class="maneuver-ts-group-plots maneuver-time-series maneuver-ts-plots-root"
                      data-group={gk}
                      ref={(el) => {
                        if (el) {
                          groupPlotMounts.set(gk, el);
                        } else {
                          groupPlotMounts.delete(gk);
                        }
                      }}
                    />
                  </div>
                )}
            </For>
          </div>
        </Show>
      </div>
      <Portal mount={typeof document !== "undefined" ? document.body : undefined}>
        <div
          id="maneuver-timeseries-tooltip"
          class="tooltip"
          style={{
            opacity: tooltip().visible ? 1 : 0,
            left: `${tooltip().x}px`,
            top: `${tooltip().y}px`,
            position: "fixed",
            "pointer-events": "none",
            "z-index": 9999,
          }}
          innerHTML={sanitizeTooltipHtml(tooltip().content)}
        />
      </Portal>
    </>
  );
}
